// ─────────────────────────────────────────────────────────────
// AiPanel — shared resizable AI assistant sidebar.
// Used on Welcome, Python, and Output tabs.
// On the Python tab, snap points apply (sidebar / split / chat-primary).
// In Chat mode (fullWidth=true), the drag handle is hidden and the panel
// fills its container; a "View Code" chip appears in the header.
// ─────────────────────────────────────────────────────────────

import { useCallback, useEffect, useRef, useState } from "react";
import { libraryScriptsApi, marieApi } from "../../api/client";
import type { MarieChatMetaFrame, TierLimitError, UpstreamError } from "../../api/client";
import { UpstreamErrorBubble } from "../../components/UpstreamErrorBubble";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { MarieIcon } from "../../components/MarieIcon";
import { MarkdownChatBody } from "../../components/MarkdownChatBody";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import AddCommentOutlinedIcon from "@mui/icons-material/AddCommentOutlined";
import CodeOutlinedIcon from "@mui/icons-material/CodeOutlined";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import AutoFixHighOutlinedIcon from "@mui/icons-material/AutoFixHighOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import CloseIcon from "@mui/icons-material/Close";
import RestoreOutlinedIcon from "@mui/icons-material/RestoreOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import type { ChatMessage, AiModel, ModelCapability } from "./types";
import { AI_MODELS } from "./types";
import { v4 as uuid } from "uuid";

// Snap widths for the Python tab (as fraction of total container width)
const SNAP_SIDEBAR = 360;          // px — default sidebar
const SNAP_SPLIT_FRACTION = 0.5;   // 50% split
const SNAP_CHAT_FRACTION = 0.70;   // chat-primary

const MIN_PANEL_WIDTH = 250;

// Snap threshold: within 60px of a snap point → snap
const SNAP_THRESHOLD = 60;

// ── Auto-image-analysis acknowledgment ─────────────────────────
// Immediate, first-person "I'm looking" line shown synchronously the instant
// the user selects/uploads an image — BEFORE the LLM request — so the thinking
// dots that follow have context. Light variety keeps repeated selections from
// feeling robotic; all stay simple and in Marie's voice.
const IMAGE_ANALYSIS_ACKS = [
  "Let me take a look at that image for you…",
  "Taking a look at your image now…",
  "Let me have a look at this image…",
  "Got it — let me look over that image…",
];
function pickImageAnalysisAck(): string {
  return IMAGE_ANALYSIS_ACKS[Math.floor(Math.random() * IMAGE_ANALYSIS_ACKS.length)];
}

// ── Simulated AI code response ─────────────────────────────────
const DEMO_CODE_INTRO = "Here's a script that thresholds your image using the MapsBridge API:";

const DEMO_CODE_SNIPPET = `import MapsBridge as mb

def run(bridge):
    image = bridge.get_current_image()
    result = bridge.process(image, method="threshold", value=128)
    bridge.set_output(result)
    return {
        "status": "ok",
        "pixels_processed": image.width * image.height,
    }`;

// Stream speed: append one character every N ms
const STREAM_CHAR_INTERVAL_MS = 18;

// ── "Marie is thinking…" indicator config ─────────────────────
// Min silence between the last token and a code_update before we surface
// the indicator pill. Below this threshold the existing blinking cursor
// is the right signal; above it, the user starts to wonder if the app
// froze (LLM tool-use composing the code can take 3–8 s).
const THINKING_THRESHOLD_MS = 500;
// Pill re-evaluation tick. Cheap state bump so derived `isThinking`
// re-runs against Date.now() without us writing a custom listener.
const THINKING_TICK_MS = 500;

// ── Periodic "still working" reassurance during long silent waits ──
// Reasoning models can sit silent for 20–60 s before the first token, and the
// tool-call "writing your script" gap can stretch too. The bouncing dots /
// code animation say "alive" but not "still on YOUR request". So once the
// stream has been open for a while WITHOUT visible content streaming yet, we
// surface a short, escalating line inside the same pill — in Marie's voice,
// honest (no fake ETA, never "almost done").
//
// This line is EPHEMERAL UI ONLY. It is derived from elapsed wall-clock time
// (streamStartedAt → tickNow); it is never written into a ChatMessage, never
// part of the history sent to the backend, and never captured as a turn.
//
// Thresholds are elapsed-since-request-sent (ms). The line that shows is the
// last threshold whose ms have elapsed; below the first threshold no line
// shows (fast replies never see it). Steady at the final step past 60 s — we
// don't know how long it'll take, so we stop escalating rather than lie.
const REASSURANCE_STEPS: { afterMs: number; text: string }[] = [
  { afterMs: 15_000, text: "I'm still working on it…" },
  { afterMs: 30_000, text: "This is taking me a little longer — hang tight…" },
  { afterMs: 45_000, text: "Bigger request — I'm still on it, thanks for your patience…" },
];

/**
 * Pick the reassurance line for the current elapsed wait, or null if it's too
 * early (under the first threshold) or we don't have a start time yet.
 * Returns the text of the last step whose `afterMs` has elapsed.
 */
function reassuranceFor(streamStartedAt: number | null, now: number): string | null {
  if (streamStartedAt === null) return null;
  const elapsed = now - streamStartedAt;
  let line: string | null = null;
  for (const step of REASSURANCE_STEPS) {
    if (elapsed >= step.afterMs) line = step.text;
  }
  return line;
}

// Phrases Marie uses right before tool-calling for code. When the streamed
// text ends with one of these, we flip the indicator to "writing your script…"
// immediately rather than waiting for the silence heuristic. Anchored to the
// END of the assembled message so we don't false-positive on phrases that
// appear mid-explanation but were not the final intent.
//
// Kept as a module-level const so it's easy to tweak as Marie's phrasing
// drifts. Patterns are case-insensitive; the test trims trailing punctuation
// and inspects the last ~200 chars only.
const CODE_INCOMING_PATTERNS: RegExp[] = [
  /\bi['']?ll generate\b/i,
  /\bi['']?ll write\b/i,
  /\bi['']?ll create the script\b/i,
  /\blet me generate\b/i,
  /\blet me write\b/i,
  /\bhere['']?s the script\b/i,
  /\bhere['']?s your script\b/i,
  /\bgenerating the script\b/i,
  /\bwriting the script\b/i,
];

/**
 * Heuristic: does this assistant text look like Marie is about to write code?
 * Inspects the trailing slice (last 240 chars) so a "I'll generate" buried at
 * the start of a long explanation doesn't keep the indicator armed forever
 * once the actual ending pivots elsewhere — but a mid-stream "I'll generate"
 * sentence still gets caught while it is the trailing content.
 */
function isCodeIncoming(text: string): boolean {
  if (!text) return false;
  const tail = text.slice(-240);
  return CODE_INCOMING_PATTERNS.some((re) => re.test(tail));
}

function getStoredUser(): { initials: string; fullName: string | null } {
  try {
    const raw = localStorage.getItem("auth_user");
    const user = raw ? JSON.parse(raw) : null;
    const fullName: string | null = user?.full_name ?? null;
    if (fullName) {
      // Reuse the comma-aware helpers so we get the right initials for
      // BOTH "Firstname Lastname" and the corporate "Lastname, Firstname [M]"
      // format used by TF Entra SSO. Pre-fix this returned "CJ" for
      // "Clark, Greg J" — wrong. Now it returns "GC".
      const first = extractFirstName(fullName);
      const last = extractLastName(fullName);
      const initials = first && last
        ? (first[0] + last[0]).toUpperCase()
        : first
          ? first[0].toUpperCase()
          : "U";
      return { initials, fullName };
    }
    if (user?.email) return { initials: user.email[0].toUpperCase(), fullName: null };
  } catch { /* ignore */ }
  return { initials: "U", fullName: null };
}

/**
 * Extract a user's first name from a full-name string.
 * Handles both "Firstname Lastname" and the corporate "Lastname, Firstname"
 * format used by TF Entra SSO (e.g. "Clark, Greg" → "Greg").
 */
export function extractFirstName(fullName: string | null): string | null {
  if (!fullName) return null;
  const trimmed = fullName.trim();
  if (!trimmed) return null;
  if (trimmed.includes(",")) {
    // "Lastname, Firstname [Middle]" — take the part after the comma
    const afterComma = trimmed.split(",", 2)[1]?.trim();
    if (afterComma) {
      return afterComma.split(/\s+/)[0];
    }
  }
  return trimmed.split(/\s+/)[0];
}

/**
 * Extract a user's last name from a full-name string.
 * Mirror of `extractFirstName` — comma-aware. For
 * "Lastname, Firstname [Middle]" the last name is the part BEFORE the comma;
 * for "Firstname [Middle ...] Lastname" it's the LAST whitespace token.
 * Returns null when the input is empty or contains only one name token.
 */
export function extractLastName(fullName: string | null): string | null {
  if (!fullName) return null;
  const trimmed = fullName.trim();
  if (!trimmed) return null;
  if (trimmed.includes(",")) {
    // "Lastname, Firstname [Middle]" — take the part before the comma
    const beforeComma = trimmed.split(",", 2)[0]?.trim();
    return beforeComma || null;
  }
  // "Firstname [Middle ...] Lastname" — take the last whitespace token,
  // but only if there's more than one token (single-name → null).
  const parts = trimmed.split(/\s+/);
  return parts.length >= 2 ? parts[parts.length - 1] : null;
}

function getUserInitials(): string {
  return getStoredUser().initials;
}

interface AiPanelProps {
  /** Container width for calculating snap fractions */
  containerWidth: number;
  /** Whether snap points should apply (Python tab only) */
  enableSnap?: boolean;
  /** Initial width in px */
  initialWidth?: number;
  /** Callback when width changes (lets parent know to resize editor) */
  onWidthChange?: (width: number) => void;
  /**
   * When true the panel fills its parent container (Chat mode).
   * The drag handle is hidden. The panel renders a "View Code" button
   * in the header.
   */
  fullWidth?: boolean;
  /** Called when the user clicks "View Code" — parent switches to Code mode */
  onViewCode?: () => void;
  /**
   * Called when the streaming simulation finishes with the final code string.
   * Parent writes this into editor state.
   */
  onCodeStreamed?: (code: string) => void;
  /**
   * Shared message history — lifted to parent so it persists across mode
   * switches without remounting this component.
   */
  messages: ChatMessage[];
  onMessagesChange: (messages: ChatMessage[]) => void;
  /**
   * Called when the user clicks the "Run Script" inline action chip.
   * Placeholder — wired to console.log until execution is live.
   */
  onRunScript?: () => void;
  /**
   * Called when the user clicks the "Fix It" inline action chip.
   * Placeholder — wired to console.log until AI fix flow is live.
   */
  onFixIt?: () => void;
  /**
   * Called to inject Python code into the Monaco Editor.
   * Always called automatically after a code block finishes streaming,
   * in both Code mode and Chat mode.
   */
  onInjectCode?: (code: string) => void;
  /**
   * Called when the user clicks the "Restore" chip on an assistant message
   * with `extractedCode` (in-session undo). The parent should mirror its
   * `onInjectCode` behavior — write the snapshot back into Monaco and
   * update the lifted code state — without persisting anything.
   *
   * Restore appears on every code-emitting message EXCEPT the one whose code
   * is currently in the editor (the "active" version, marked with a "Current"
   * chip). The user can ping-pong freely between any prior versions: clicking
   * Restore on an older message makes it the new active, and the previously
   * active message gains its own Restore chip so the user can come back.
   */
  onRestoreCode?: (code: string) => void;
  /**
   * Whether the panel is currently in Chat mode (fullWidth=true means Chat mode
   * from PythonTab's perspective).
   */
  editorMode?: "code" | "chat";
  /** Whether a script is currently executing — drives the Run Script spinner. */
  isRunning?: boolean;
  /**
   * When non-null, automatically sends this text to Marie for error analysis.
   * Parent resets to null via onAutoAnalyzeErrorConsumed after it has been consumed.
   */
  autoAnalyzeError?: string | null;
  onAutoAnalyzeErrorConsumed?: () => void;
  /**
   * Proactive auto-image-analysis trigger (Mia, 2026-06-05). Bumped by the
   * parent (PythonTab) ONLY when the user actively picks a library image or
   * uploads a temp image for the editor's input — NOT when a starter script
   * auto-pairs its image. When this counter changes, AiPanel sends a silent
   * `auto_image_analysis: true` turn to Marie (current image attached via the
   * getters, current marie_session_id) and renders ONLY her streamed reply as
   * an assistant message — no synthetic user bubble. The `key`'s value carries
   * a stable identity string for the image so AiPanel can dedupe re-fires for
   * the same image. Parent resets nothing; AiPanel guards on the counter +
   * identity itself.
   */
  autoAnalyzeImageKey?: number;
  /** Stable identity of the image to auto-analyze (library id or temp file ref). Used for dedupe. */
  autoAnalyzeImageIdentity?: string | null;
  /** When non-null, auto-sends this as a visible user message to Marie. */
  pendingPrompt?: string | null;
  onPendingPromptConsumed?: () => void;
  /**
   * Callback that returns the current Monaco editor contents at call time.
   * Called at message-send time so Marie always has the latest code,
   * not a stale snapshot from a previous render.
   */
  getEditorCode?: () => string;
  /**
   * Callback that returns the user_images.id of the currently-selected
   * image at call time, or null when no image is selected. The Marie
   * backend uses this to inject acquisition metadata (pixel size, kV,
   * dwell, etc.) into her context — see backend/image_metadata.py.
   */
  getSelectedImageId?: () => number | null;
  /**
   * Callback that returns the source of the currently-selected image —
   * "user" for an image from the personal library, "library" for a
   * shared/system-library image. Drives the `input_image_source` field
   * on Marie vision-attach requests; backend rejects mismatched pairs.
   */
  getSelectedImageSource?: () => "user" | "library" | null;
  /**
   * Callback that returns the human-readable filename of the currently-
   * selected image (used as the chip label in the "image attached"
   * affordance). Returns null when no image is selected.
   */
  getSelectedImageName?: () => string | null;
  /**
   * Callback that returns the in-memory `File` for an image picked via
   * the "Upload temp image" button — i.e. an image that lives only in
   * the browser session and was never persisted to the user library.
   * Returns null when no temp file is selected. Marie sees these by
   * uploading the bytes as a multipart `image_attachments[]` entry on
   * the chat request (Sam's 2026-05-07 endpoint cutover); the
   * `getSelectedImageId`/`Source` path stays for library/user picks.
   */
  getTempImageFile?: () => File | null;
  /**
   * Called when a hero-state suggestion chip should load a starter script
   * into the editor (same signature the rest of the app uses). Optional so
   * OutputTab — which never reaches hero state — can continue to render
   * AiPanel without providing a loader. If absent, script-loading chips
   * fall back to sending their label as a plain chat prompt.
   */
  onLoadScript?: (
    code: string,
    defaultImageId?: number | null,
    savedScriptId?: number | null,
    scriptTitle?: string,
  ) => void;
  /**
   * ── Marie session capture (Sam, 2026-05-06) ──
   * Active session id for this app instance, captured from the leading meta
   * frame on the first chat in a session. Sent back on every subsequent
   * chat so the backend can stitch turns into one MarieSession.
   */
  marieSessionId?: number | null;
  /** Setter — AiPanel calls this when it learns the session id from a meta frame. */
  onMarieSessionIdChange?: (id: number | null) => void;
  /**
   * Bumped by the parent every time a "new session" should start (Start new
   * chat, script load, sign-in). When this changes, AiPanel arms its local
   * "send new_session=true on the next chat" flag.
   */
  newSessionArmKey?: number;
  /**
   * Optional: called when AiPanel itself triggers a new session (e.g. from
   * its own "Start new chat" button). Lets the parent reset its mirror.
   */
  onArmNewSession?: () => void;
  /**
   * Saved-script id currently loaded in the editor (or null for new/starter
   * scripts). Sent on every chat so the backend can associate turns with
   * the script the user is iterating on.
   */
  loadedScriptId?: number | null;
  /**
   * Called by "Start new chat" AFTER the local chat/session reset, to also
   * wipe the surrounding workspace (Monaco editor content, selected image,
   * loadedScriptId). When provided, New Chat performs a "full reset" — the
   * user is back to the same state as a fresh sign-in. When omitted, New
   * Chat only resets the chat history + arms a fresh Marie session (legacy
   * behavior). The parent is responsible for any tab-switching decisions.
   */
  onClearWorkspace?: () => void;
  /**
   * Per-user list of Marie model ids the caller is allowed to send (from
   * `/api/auth/me` → `allowed_models`). Sam (2026-05-14) — `basic` tier
   * gets the configured subset; `advanced`/`admin` get every catalog id.
   *
   * Filtering rules:
   *   • The picker dropdown only renders entries from AI_MODELS whose `id`
   *     is in this array.
   *   • If the persisted `selectedModel` isn't allowed (e.g. an `advanced`
   *     user was demoted to `basic` while their localStorage still has
   *     Opus), silently fall back to the first allowed id and persist.
   *     Never block the user with an error.
   *
   * When `null` / undefined / empty, every catalog model is shown (no
   * filter). This is the steady-state fallback for unauthenticated paths
   * or pre-2026-05-14 cached `auth_user` blobs that lack the field.
   */
  allowedModels?: string[] | null;
  /**
   * Called when the chat is rejected by a tier-limit gate (Sam, 2026-05-14):
   *   • 403 model_not_allowed — `kind: "model_not_allowed"`, the picker
   *     should auto-switch to an allowed model.
   *   • 429 quota_exceeded — `kind: "quota_exceeded_marie_chat"`, the send
   *     button should be disabled for the remainder of the session and
   *     the caller toasts the friendly copy.
   *
   * The parent owns the actual Snackbar so positioning matches the
   * "no image selected" toast Greg already liked. AiPanel still
   * locally tracks the marie-chat lock so the input area can render
   * its disabled state.
   */
  onTierLimitToast?: (info: {
    kind: "model_not_allowed" | "quota_exceeded_marie_chat";
    message: string;
  }) => void;
  /**
   * Currently-selected Marie model id. Lifted to the parent
   * (ScriptHelperApp) so the value is shared across every AiPanel instance
   * — PythonTab Code-mode sidebar, PythonTab Chat-mode full-width, and
   * OutputTab sidebar. Each tab used to own its own copy of this state,
   * which meant switching tabs (or auto-switching to Output after a run)
   * reverted the picker to the default. See Greg's bug 2026-05-15.
   */
  selectedModel: string;
  /**
   * Setter for the lifted model selection. Wired to the Select onChange in
   * the header, to the tier-limit auto-fallback (handleMarieTierLimit),
   * and to the auto-recover-on-allowedModels-change effect.
   */
  onSelectedModelChange: (modelId: string) => void;
}

const PLACEHOLDER_MESSAGES: ChatMessage[] = [
  {
    id: "1",
    role: "assistant",
    content:
      "Hello! I'm Marie, your AI coding assistant for the Maps microscopy platform. I can help you write and debug Python scripts using the Maps Script Bridge API. What would you like to work on?",
  },
];

const QUICK_REPLIES = [
  "Explain this script",
  "Find bugs",
  "Add comments",
  "Optimize performance",
];

// ── Helper: detect Marie's two mandatory clarifying questions ───
// Returns button options to render below the bubble, or null.
function detectClarifyingButtons(content: string): { label: string; reply: string }[] | null {
  const lower = content.toLowerCase();

  // Q2 check first — more specific, must come before Q1 check
  // "single-tile mode or batch mode", "single tile or batch", etc.
  const isQ2 =
    (lower.includes("single-tile") || lower.includes("single tile")) &&
    lower.includes("batch");

  if (isQ2) {
    return [
      { label: "Single-tile mode", reply: "Single-tile mode please" },
      { label: "Batch mode", reply: "Batch mode please" },
    ];
  }

  // Q1 — "tile set or image layer" (or reverse)
  const isQ1 =
    lower.includes("tile set") &&
    lower.includes("image layer") &&
    (lower.includes("or") || lower.includes("?"));

  if (isQ1) {
    return [
      { label: "Tile Set", reply: "Tile Set" },
      { label: "Image Layer", reply: "Image Layer" },
    ];
  }

  return null;
}

// ── Helper: extract the largest ```python ... ``` block from text ──
// When Marie includes both a snippet and the full corrected script, we want
// the full script — which is reliably the longer block. Picking the first
// match would inject the snippet and silently overwrite the editor with
// incomplete code.
function extractPythonCodeBlock(text: string): string | null {
  const matches = [...text.matchAll(/```python\s*\n([\s\S]*?)```/g)];
  // eslint-disable-next-line no-console
  console.log("[Marie extract] python blocks found:", matches.length, "lengths:", matches.map((m) => m[1].length));
  if (matches.length === 0) {
    // Fallback: try generic ``` blocks (Marie sometimes drops the python tag).
    const generic = [...text.matchAll(/```\s*\n([\s\S]*?)```/g)];
    // eslint-disable-next-line no-console
    console.log("[Marie extract] fallback generic blocks:", generic.length, "lengths:", generic.map((m) => m[1].length));
    if (generic.length === 0) return null;
    let best = generic[0][1];
    for (const m of generic) if (m[1].length > best.length) best = m[1];
    return best.trimEnd() || null;
  }
  let best = matches[0][1];
  for (const m of matches) if (m[1].length > best.length) best = m[1];
  const result = best.trimEnd();
  const channelLine = result.split("\n").find((l) => l.includes("create_channel")) ?? "(no create_channel line)";
  // eslint-disable-next-line no-console
  console.log("[Marie extract] chosen block (first 120 chars):", result.slice(0, 120));
  // eslint-disable-next-line no-console
  console.log("[Marie extract] create_channel line:", channelLine);
  return result || null;
}

// ── Helper: strip code fence content from text for display ──────
// Handles three cases:
//   1. Complete fence  ```python\n...\n```  → removed entirely
//   2. Partial fence that has opened but not closed → everything from ``` onward removed
//   3. No fence present → text returned unchanged
function stripCodeFenceForDisplay(text: string): string {
  // Remove complete python-tagged fences
  let stripped = text.replace(/```python[\s\S]*?```/g, "");
  // Remove complete generic fences (``` with no language tag — requires newline after backticks
  // to avoid accidentally matching inline triple-backtick runs that aren't code fences)
  stripped = stripped.replace(/```\s*\n[\s\S]*?```/g, "");
  // If an opening fence is still present (unclosed — mid-stream or malformed), remove from it to end
  const openIdx = stripped.indexOf("```");
  if (openIdx !== -1) {
    stripped = stripped.slice(0, openIdx);
  }
  return stripped.trimEnd();
}

// ── Capability badge ──────────────────────────────────────────────────
// Small icon shown to the LEFT of the model label in the picker (and in
// the closed-state trigger button). Icon SHAPE carries the semantic —
// AutoAwesome (sparkle) for reasoning, Bolt (lightning) for fast — so we
// keep colour neutral (`text.secondary`) and let the lock icon on the
// right own the loud visual lane.
function CapabilityIcon({
  capability,
  fontSize = 16,
}: {
  capability: ModelCapability;
  fontSize?: number;
}) {
  const Icon = capability === "fast" ? BoltOutlinedIcon : AutoAwesomeOutlinedIcon;
  return (
    <Icon
      sx={{
        fontSize,
        color: "text.secondary",
        flexShrink: 0,
      }}
    />
  );
}

const CAPABILITY_TOOLTIP_COPY: Record<ModelCapability, string> = {
  reasoning: "Advanced reasoning — slower but more capable",
  fast: "Fast — no reasoning overhead, great for quick iterations",
};

export function AiPanel({
  containerWidth,
  enableSnap = false,
  initialWidth = SNAP_SIDEBAR,
  onWidthChange,
  fullWidth = false,
  onViewCode,
  onCodeStreamed,
  messages,
  onMessagesChange,
  onRunScript,
  onFixIt,
  onInjectCode,
  onRestoreCode,
  editorMode = "code",
  isRunning = false,
  autoAnalyzeError,
  onAutoAnalyzeErrorConsumed,
  autoAnalyzeImageKey,
  autoAnalyzeImageIdentity,
  pendingPrompt,
  onPendingPromptConsumed,
  getEditorCode,
  getSelectedImageId,
  getSelectedImageSource,
  getSelectedImageName,
  getTempImageFile,
  onLoadScript,
  marieSessionId,
  onMarieSessionIdChange,
  newSessionArmKey,
  onArmNewSession,
  loadedScriptId,
  onClearWorkspace,
  allowedModels,
  onTierLimitToast,
  selectedModel,
  onSelectedModelChange,
}: AiPanelProps) {
  const [width, setWidth] = useState(initialWidth);

  // ── Per-user allowed-model membership ──────────────────────────
  // Sam (2026-05-14) — `/api/auth/me` returns `allowed_models`. As of
  // 2026-05-15 the picker no longer hides restricted models — it shows
  // every catalog entry but renders the non-allowed rows disabled with
  // an explanatory tooltip (Greg's call: "convey the value of upgrading
  // without burying the feature list"). Backend gate is still the source
  // of truth — this is purely UX.
  //
  // `allowedSet` is null when no whitelist is in force (legacy callers,
  // cached pre-2026-05-14 user blobs, unauth paths, admin/advanced tiers
  // that get the full catalog). In that case every row is enabled.
  //
  // Safety net: if `allowedModels` is non-empty but intersects nothing
  // in AI_MODELS, treat it as "no whitelist" rather than disabling every
  // row — the backend would still enforce, but rendering an entirely
  // disabled picker would lock the user out client-side with no way
  // forward.
  const allowedSet = (() => {
    if (!allowedModels || allowedModels.length === 0) return null;
    const set = new Set(allowedModels);
    const anyMatch = AI_MODELS.some((m) => set.has(m.id));
    return anyMatch ? set : null;
  })();
  const hasDisabledModels =
    allowedSet !== null && AI_MODELS.some((m) => !allowedSet.has(m.id));

  // Selected Marie model is lifted to the parent (ScriptHelperApp) so every
  // AiPanel instance (PythonTab Code sidebar, PythonTab Chat full-width,
  // OutputTab sidebar) shares one value. The parent also owns localStorage
  // persistence and the initial hydrate, including the basic-user fallback
  // when the persisted id isn't in allowedModels. We just read `selectedModel`
  // and call `onSelectedModelChange` from the picker / tier-limit / allowed-
  // models-change paths.

  // Session-level lock when the basic-tier daily Marie cap has been hit
  // (Sam, 2026-05-14). Sticky for the rest of the page session — the
  // counter resets at UTC midnight, but the UX guarantee is "we told you
  // you're out; don't let you keep trying until you reload". A page
  // refresh / re-login clears the lock and the backend gate re-checks.
  const [marieChatLocked, setMarieChatLocked] = useState(false);

  // (Auto-recover-from-disallowed-selectedModel now lives in ScriptHelperApp
  // alongside the lifted state, so the reconciliation runs once per change
  // rather than once per mounted AiPanel instance.)
  const [inputValue, setInputValue] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [inputSlideIn, setInputSlideIn] = useState(false);

  // ── "Marie is thinking…" indicator state ───────────────────────
  // lastTokenAt    — wall-clock ms of the most recent token frame (null until
  //                  the first token arrives for the active stream).
  // expectingCode  — set true once isCodeIncoming() matches the streaming
  //                  text (mid-stream OR at the post-token pause), or once
  //                  we've actually received a code_update frame.
  // codeUpdateReceived — code_update arrived but the stream hasn't said done.
  //                  Marie may still be appending wrap-up text; keep the pill.
  // tickNow        — bumped by a 500 ms interval while streaming so the
  //                  derived `isThinking` flag re-evaluates against Date.now()
  //                  without us subscribing to a heavier source of time.
  const [lastTokenAt, setLastTokenAt] = useState<number | null>(null);
  const [expectingCode, setExpectingCode] = useState(false);
  const [codeUpdateReceived, setCodeUpdateReceived] = useState(false);
  const [tickNow, setTickNow] = useState<number>(() => Date.now());
  // streamStartedAt — wall-clock ms when the current request was sent. Drives
  // the elapsed-time "still working" reassurance line (see REASSURANCE_STEPS).
  // Set in resetThinkingIndicator() at each send; cleared (null) on done/error
  // so a finished/failed stream never shows a stale reassurance line.
  const [streamStartedAt, setStreamStartedAt] = useState<number | null>(null);

  // ── Per-turn image attach toggle ───────────────────────────────
  // True by default whenever an image is selected (library/user pick OR
  // a "Upload temp image" disk file). Flips to false ONLY when the user
  // clicks the X on the chip — and only for the next message. We re-arm
  // to true automatically whenever the selected image identity changes
  // (different library id, OR a different File instance from disk) so the
  // user never has to remember to re-enable it. Greg's instruction: no
  // persistent toggle.
  const [attachImageThisTurn, setAttachImageThisTurn] = useState(true);
  // Track the previous "image identity" — for library/user picks this is
  // the numeric id; for temp files it's the File instance reference.
  // We watch both so the re-arm fires on either kind of selection change.
  const lastAttachedImageIdRef = useRef<number | null>(null);
  const lastAttachedTempFileRef = useRef<File | null>(null);

  // Poll the parent's selection on every render. Cheap — these getters are
  // just `selectedImage?.field` / `tempImageFile` reads. Stored in locals so
  // the chip render + the effect below can read consistent values.
  const currentImageId = getSelectedImageId?.() ?? null;
  const currentImageSource = getSelectedImageSource?.() ?? null;
  const currentImageName = getSelectedImageName?.() ?? null;
  const currentTempFile = getTempImageFile?.() ?? null;

  // When the selected image identity changes (different library id, or a
  // new File instance from disk, or null↔something transition on either),
  // re-arm the per-turn attach. Skip the very first mount when nothing is
  // selected so we don't trip the effect spuriously.
  useEffect(() => {
    const idChanged = lastAttachedImageIdRef.current !== currentImageId;
    const fileChanged = lastAttachedTempFileRef.current !== currentTempFile;
    if (idChanged || fileChanged) {
      lastAttachedImageIdRef.current = currentImageId;
      lastAttachedTempFileRef.current = currentTempFile;
      setAttachImageThisTurn(true);
    }
  }, [currentImageId, currentTempFile]);
  // Per-label loading flag for hero-state chips that trigger a starter-script
  // load (currently only "False Color — Single Image"). Only the specific chip
  // shows a spinner; the other chips stay clickable.
  const [loadingScriptChipLabel, setLoadingScriptChipLabel] = useState<string | null>(null);
  // ── In-session restore (Mia, feature-script-versioning) ─────────
  // Click "Restore" on a prior assistant turn → confirm dialog → land code
  // back in the editor via onRestoreCode (parent reuses its inject path).
  // pendingRestore holds {code, msgId} while the confirm is open. The msgId
  // drives a brief inline "Restored ✓" state on the chip after success
  // (in addition to the snackbar) so the click doesn't feel like nothing
  // happened if the user's eyes are on the message they just clicked.
  const [pendingRestore, setPendingRestore] = useState<{ code: string; msgId: string } | null>(null);
  const [restoredFlashMsgId, setRestoredFlashMsgId] = useState<string | null>(null);
  const [restoreSnackbarOpen, setRestoreSnackbarOpen] = useState(false);
  // ── Active-version tracking (Mia, 2026-05-11 fix) ──────────────
  // Which message's extractedCode is currently in the editor. Drives:
  //  • The "Current" indicator chip (this is where you are).
  //  • Restore-chip suppression (no Restore on the message you're already on).
  // Initialized from `messages` on first render (covers reload-with-history),
  // then updated by a useEffect when Marie streams new code (auto-follows the
  // new latest) and by handleConfirmRestore when the user restores an older
  // version. See the useEffect below for the new-code heuristic.
  const [activeCodeMsgId, setActiveCodeMsgId] = useState<string | null>(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.extractedCode !== undefined && !m.isStreaming) return m.id;
    }
    return null;
  });
  // Last known "latest extracted-code message id" — used to detect when Marie
  // has produced a NEW code-bearing message (vs. an existing message merely
  // mutating). Lets the auto-follow effect below distinguish "new code from
  // Marie" from "same code, message edited / streaming flag flipped".
  const lastLatestCodeMsgIdRef = useRef<string | null>(activeCodeMsgId);
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragStartWidth = useRef(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const streamIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // ID of the stream whose tokens currently own the assistant bubble.
  // Each stream captures its own assistantMsgId at call time and gates its
  // callbacks against this ref — if a newer stream has taken over (or the
  // stream was superseded), older callbacks become no-ops. This replaces the
  // AbortController-on-unmount pattern, which fought React Strict Mode's
  // simulated-unmount cleanup (dev-only effect double-invocation would
  // abort freshly-started streams).
  const streamingMsgIdRef = useRef<string | null>(null);
  const pendingCodeUpdateRef = useRef<string | null>(null);
  // Tracks the assistant-message id for which we've already finalized a
  // structured <UpstreamErrorBubble>. The legacy `onError` callback always
  // fires alongside `onUpstreamError` (so non-upgraded callers still see a
  // plain-text message); when this ref matches the current streaming id we
  // suppress the legacy generic-error bubble and consume the ref. Marie's
  // three chatStream call sites all check this — see handleSend /
  // handleQuickReply / handleSilentAnalysis.
  const marieUpstreamErrorSeenRef = useRef<string | null>(null);
  // Ref that stays current with the messages prop so the interval can read it
  const messagesRef = useRef<ChatMessage[]>(messages);
  messagesRef.current = messages;

  // ── Marie session refs ────────────────────────────────────────
  // Mirrors of the props so the SSE-callback closures (captured at chat-start)
  // always see fresh values on each token/meta frame.
  const marieSessionIdRef = useRef<number | null>(marieSessionId ?? null);
  marieSessionIdRef.current = marieSessionId ?? null;

  const loadedScriptIdRef = useRef<number | null>(loadedScriptId ?? null);
  loadedScriptIdRef.current = loadedScriptId ?? null;

  // True when the next chat must carry `new_session: true`. Re-armed by the
  // parent via newSessionArmKey, by AiPanel's own handleNewChat, and on first
  // mount when there's no session yet. Cleared the moment we send a chat.
  //
  // Mount-time decision (Mia, 2026-06-05 fix for fragmented sessions): a fresh
  // AiPanel must RESUME a live session if one already exists, not start a new
  // one. The Output tab's AiPanel remounts on every run (OutputTab is
  // conditionally mounted), and a default of `true` made the first post-run
  // message open a brand-new MarieSession — splitting one conversation across
  // many rows. So we initialize armed = (no live session at mount). A genuine
  // new session is still armed explicitly: "Start new chat" / "New Script" /
  // loading another script / sign-out all bump newSessionArmKey AND null
  // marieSessionId via the parent's armNewSession() — both the arm-key effect
  // below and any subsequent remount (now seeing marieSessionId === null)
  // re-arm correctly.
  const newSessionArmedRef = useRef<boolean>(marieSessionId == null);

  // Detect parent re-arm (script load, sign-out, "Start new chat", etc). Skip
  // the very first mount — lastArmKeyRef is seeded with the current arm key, so
  // this effect only fires on a genuine parent bump, where it force-arms
  // regardless of the mount-time default computed above.
  const lastArmKeyRef = useRef<number | undefined>(newSessionArmKey);
  useEffect(() => {
    if (lastArmKeyRef.current !== newSessionArmKey) {
      lastArmKeyRef.current = newSessionArmKey;
      newSessionArmedRef.current = true;
    }
  }, [newSessionArmKey]);

  // Current "still working" reassurance line (or null). Derived from elapsed
  // wall-clock time; recomputed every tick while streaming. Hoisted here (vs.
  // only in the render IIFE) so the scroll effect below can depend on its
  // discrete value — each escalation step (null→15 s→30 s→45 s) is a meaningful
  // layout transition that should re-anchor the scroll, but the underlying
  // tickNow churns every 500 ms and must NOT be a scroll dep.
  const reassuranceLine = reassuranceFor(streamStartedAt, tickNow);

  // Scroll to bottom on new messages.
  //
  // Also re-fire on indicator transitions: when Marie enters the silent gap
  // and the "Marie is thinking…" / "writing your script…" card appears (or
  // swaps mode), the card renders BELOW the last message bubble inside the
  // same scroll container — but `messages` hasn't changed, so without these
  // extra deps the scroll-to-bottom effect doesn't fire and the (now larger)
  // indicator card sits clipped under the chat input. Depending on the
  // discrete indicator-state booleans (NOT `tickNow`, which ticks every
  // 500 ms while streaming and would cause constant scroll churn) makes the
  // scroll fire exactly on the meaningful transitions: thinking-mode appears,
  // thinking → coding-mode swap, and each reassurance escalation step.
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isStreaming, expectingCode, codeUpdateReceived, reassuranceLine]);

  // While a stream is open, bump tickNow every 500 ms so the derived
  // `isThinking` flag re-evaluates against Date.now() and the indicator pill
  // shows up after the silence threshold elapses without a token. Stops
  // ticking the moment streaming ends — no work between turns.
  useEffect(() => {
    if (!isStreaming) return;
    const id = setInterval(() => setTickNow(Date.now()), THINKING_TICK_MS);
    return () => clearInterval(id);
  }, [isStreaming]);

  // Clear any running demo-code interval on unmount. In-flight network streams
  // are NOT aborted here — they self-gate via streamingMsgIdRef, and React 18
  // makes setState-on-unmounted a no-op so late callbacks are harmless.
  useEffect(() => {
    return () => {
      if (streamIntervalRef.current !== null) {
        clearInterval(streamIntervalRef.current);
      }
    };
  }, []);

  // Auto-follow Marie's new code (Mia, 2026-05-11 fix).
  //
  // When the most recent extracted-code message id changes — i.e. Marie has
  // just streamed a NEW code-bearing turn — make that message the active one,
  // because the streaming pipe has already written its code into Monaco. This
  // also resets active correctly when the messages array shrinks/replaces
  // (New Chat → placeholder → no extracted code → null → next Marie turn
  // becomes active).
  //
  // What this effect deliberately does NOT do: react to the active id being
  // different from the latest after a manual restore. If the user restores an
  // older version, lastLatestCodeMsgIdRef stays pinned at the (still-current)
  // latest message, so this effect won't fire and won't trample their choice.
  // It only fires when a NEW latest appears (or the latest becomes null on
  // chat reset).
  useEffect(() => {
    let latestId: string | null = null;
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.extractedCode !== undefined && !m.isStreaming) {
        latestId = m.id;
        break;
      }
    }
    if (latestId !== lastLatestCodeMsgIdRef.current) {
      lastLatestCodeMsgIdRef.current = latestId;
      setActiveCodeMsgId(latestId);
    }
  }, [messages]);

  // Compute the vision-attach inputs to include on a Marie request, IF
  // an image is currently selected AND the user hasn't detached it for
  // this turn. Two parallel attach channels are supported:
  //   1. Library / user-pick — travels in the JSON payload as
  //      `input_image_id` + `input_image_source`. Backend resolves the
  //      bytes from disk by id.
  //   2. Temp file ("Upload temp image" → in-memory File) — travels as
  //      multipart `image_attachments[0]`. Backend takes the bytes
  //      straight from the upload, no DB hop required.
  // Both channels can be active in the same turn in principle, but the
  // current UI only ever has one image selected at a time, so in practice
  // exactly one is set. willAttach is true if EITHER will go.
  //
  // - willAttach is the source of truth for ChatMessage.hadImageAttached
  //   so the meta-frame UI can distinguish "no image was supposed to be
  //   attached" from "image was supposed to be attached but the backend
  //   degraded".
  // - All fields are omitted (undefined / empty array) when nothing is
  //   selected OR the user detached for this turn — backend treats
  //   absence as text-only without a 422.
  function buildImageAttachPair(): {
    input_image_id?: number;
    input_image_source?: "user" | "library";
    imageFiles: File[];
    willAttach: boolean;
  } {
    if (!attachImageThisTurn) {
      return { imageFiles: [], willAttach: false };
    }
    const result: {
      input_image_id?: number;
      input_image_source?: "user" | "library";
      imageFiles: File[];
      willAttach: boolean;
    } = { imageFiles: [], willAttach: false };

    if (currentImageId != null && currentImageSource != null) {
      result.input_image_id = currentImageId;
      result.input_image_source = currentImageSource;
      result.willAttach = true;
    }
    if (currentTempFile != null) {
      result.imageFiles.push(currentTempFile);
      result.willAttach = true;
    }
    return result;
  }

  /**
   * Build the Marie session payload bits for a chat request and consume the
   * "armed" flag. After calling this once, the next call returns
   * `new_session: false` (until handleNewChat or the parent re-arms).
   */
  function consumeMarieSessionPayload(): {
    marie_session_id: number | null;
    new_session: boolean;
    script_id: number | null;
  } {
    const armed = newSessionArmedRef.current;
    if (armed) newSessionArmedRef.current = false;
    return {
      // When armed, the backend will ignore any id and create a fresh
      // session — but pass null explicitly so we never accidentally
      // continue a stale id from a prior session that's been reset.
      marie_session_id: armed ? null : marieSessionIdRef.current,
      new_session: armed,
      script_id: loadedScriptIdRef.current,
    };
  }

  /**
   * Reset the "Marie is thinking…" indicator state at the start of a new
   * stream. Called from each of the three send paths (handleSend,
   * handleQuickReply, handleSilentAnalysis) so the pill from a prior turn
   * never bleeds into the next one.
   */
  function resetThinkingIndicator(): void {
    setLastTokenAt(null);
    setExpectingCode(false);
    setCodeUpdateReceived(false);
    setTickNow(Date.now());
    // Clear the reassurance clock. The send paths call startThinkingTimer()
    // immediately after to (re)start it for the new request; done/error paths
    // call this alone, leaving it null so no stale "still working" line shows.
    setStreamStartedAt(null);
  }

  /**
   * Start (or restart) the elapsed-time clock that drives the periodic
   * "still working" reassurance line. Called by each send path right after
   * resetThinkingIndicator() so every new request gets a fresh ~15 s cadence
   * and a fast follow-up never inherits a stale reassurance message.
   */
  function startThinkingTimer(): void {
    const now = Date.now();
    setStreamStartedAt(now);
    setTickNow(now);
  }

  /**
   * Marie chat tier-limit handler (Sam, 2026-05-14). Shared by all three
   * chatStream call sites so the UX response is identical regardless of
   * which send path triggered the reject.
   *
   *   • 403 model_not_allowed — auto-switch the picker to the first allowed
   *     model so the next send won't 403 again. Toast the friendly copy.
   *   • 429 quota_exceeded / kind=marie_chat — flip the session-level
   *     marieChatLocked flag (sticky until reload) so the input greys
   *     out + a persistent inline hint renders above it. Toast.
   *
   * The `script_run` quota_exceeded variant doesn't fire here — it's
   * raised by /api/execute, surfaced by ScriptHelperApp.handleRun, and
   * toasted at the app root with a bottom-center Snackbar matching the
   * "no image selected" pattern.
   */
  function handleMarieTierLimit(err: TierLimitError): void {
    if (err.detail.code === "model_not_allowed") {
      const { allowed_models } = err.detail;
      if (allowed_models.length > 0) {
        const fallback =
          AI_MODELS.find((m) => allowed_models.includes(m.id)) ??
          ({ id: allowed_models[0], label: allowed_models[0], capability: "fast" } as AiModel);
        onSelectedModelChange(fallback.id);
      }
      onTierLimitToast?.({ kind: "model_not_allowed", message: err.detail.message });
      return;
    }
    if (err.detail.code === "quota_exceeded" && err.detail.kind === "marie_chat") {
      setMarieChatLocked(true);
      onTierLimitToast?.({
        kind: "quota_exceeded_marie_chat",
        message: err.detail.message,
      });
    }
  }

  /**
   * Handle the meta frame for a Marie chat stream. Captures the assigned
   * `marie_session_id` into the parent state on first sight, and updates
   * the local ref immediately so a rapid follow-up turn sends the right
   * id even before the parent re-render.
   */
  function handleMarieMeta(meta: MarieChatMetaFrame): void {
    const incoming = meta.marie_session_id ?? null;
    if (incoming != null && incoming !== marieSessionIdRef.current) {
      marieSessionIdRef.current = incoming;
      onMarieSessionIdChange?.(incoming);
    }
  }

  function getSnapWidth(rawWidth: number): number {
    if (!enableSnap || containerWidth <= 0) return rawWidth;
    const splitPx = containerWidth * SNAP_SPLIT_FRACTION;
    const chatPrimaryPx = containerWidth * SNAP_CHAT_FRACTION;

    // Check proximity to snap points
    if (Math.abs(rawWidth - SNAP_SIDEBAR) < SNAP_THRESHOLD) return SNAP_SIDEBAR;
    if (Math.abs(rawWidth - splitPx) < SNAP_THRESHOLD) return splitPx;
    if (Math.abs(rawWidth - chatPrimaryPx) < SNAP_THRESHOLD) return chatPrimaryPx;
    return rawWidth;
  }

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isDragging.current = true;
    dragStartX.current = e.clientX;
    dragStartWidth.current = width;

    function onMouseMove(ev: MouseEvent) {
      if (!isDragging.current) return;
      const delta = dragStartX.current - ev.clientX; // dragging left handle: move left = wider
      const raw = Math.max(
        MIN_PANEL_WIDTH,
        Math.min(containerWidth * 0.75, dragStartWidth.current + delta)
      );
      setWidth(raw);
      onWidthChange?.(raw);
    }

    function onMouseUp(ev: MouseEvent) {
      isDragging.current = false;
      // Apply snap on release
      const delta = dragStartX.current - ev.clientX;
      const raw = Math.max(
        MIN_PANEL_WIDTH,
        Math.min(containerWidth * 0.75, dragStartWidth.current + delta)
      );
      const snapped = getSnapWidth(raw);
      setWidth(snapped);
      onWidthChange?.(snapped);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    }

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width, containerWidth, enableSnap, onWidthChange]);

  function handleSend() {
    const trimmed = inputValue.trim();
    if (!trimmed || isStreaming) return;
    // Sticky session lock — backend already toasted "daily limit reached"
    // once; don't let keyboard fast-paths slip a second request through.
    if (marieChatLocked) return;

    const userMsg: ChatMessage = { id: uuid(), role: "user", content: trimmed };

    // Build history from current messages (exclude the message being sent now).
    // Filter out checkpoint rows — they're client-side timeline markers, not
    // chat turns Marie should see in her conversation context.
    const history = messages
      .filter((m): m is ChatMessage & { role: "user" | "assistant" } => m.role !== "checkpoint")
      .map((m) => ({ role: m.role, content: m.content }));

    // Resolve the vision-attach pair once; reused for the network payload
    // and stamped on the assistant placeholder so the meta-frame UI can
    // tell whether an image was *meant* to be attached for this turn.
    const attach = buildImageAttachPair();

    // After sending, the per-turn detach has done its job — re-arm so the
    // next message auto-attaches again. (Keeps Greg's "no persistent opt-
    // out" rule even after a manual detach.)
    if (!attachImageThisTurn) setAttachImageThisTurn(true);

    // Add user message and a blank streaming assistant placeholder
    const assistantMsgId = uuid();
    const assistantMsg: ChatMessage = {
      id: assistantMsgId,
      role: "assistant",
      content: "",
      isStreaming: true,
      hadImageAttached: attach.willAttach,
    };

    const myMsgId = assistantMsgId;
    streamingMsgIdRef.current = myMsgId;
    onMessagesChange([...messages, userMsg, assistantMsg]);
    setInputValue("");
    setIsStreaming(true);
    resetThinkingIndicator();
    startThinkingTimer();

    const sessionPayload = consumeMarieSessionPayload();

    marieApi.chatStream(
      {
        message: trimmed,
        history,
        model: selectedModel,
        current_code: getEditorCode?.(),
        user_name: getStoredUser().fullName ?? undefined,
        image_id: getSelectedImageId?.() ?? null,
        input_image_id: attach.input_image_id,
        input_image_source: attach.input_image_source,
        ...sessionPayload,
      },
      {
        onMeta: (meta) => {
          if (streamingMsgIdRef.current !== myMsgId) return;
          handleMarieMeta(meta);
          const next = messagesRef.current.map((m) =>
            m.id === myMsgId ? { ...m, meta } : m,
          );
          messagesRef.current = next;
          onMessagesChange(next);
        },
        onToken: (token) => {
          if (streamingMsgIdRef.current !== myMsgId) return; // superseded stream
          const next = messagesRef.current.map((m) =>
            m.id === myMsgId ? { ...m, content: m.content + token } : m
          );
          // Sync the ref immediately so the next rapid onToken call reads the
          // freshly-accumulated content instead of a stale render snapshot.
          // Without this, tokens arriving faster than React re-renders get
          // dropped/mangled (each callback reads the same old content).
          messagesRef.current = next;
          onMessagesChange(next);
          // Indicator: refresh the silence timer and run the pattern check
          // against the freshly accumulated message so a mid-stream
          // "I'll generate" sentence flips us to "writing your script…"
          // immediately, before the post-token pause.
          setLastTokenAt(Date.now());
          const updated = next.find((m) => m.id === myMsgId);
          if (updated && isCodeIncoming(updated.content)) {
            setExpectingCode(true);
          }
        },
        onCodeUpdate: (code) => {
          if (streamingMsgIdRef.current !== myMsgId) return;
          pendingCodeUpdateRef.current = code;
          setCodeUpdateReceived(true);
          if (onInjectCode) onInjectCode(code);
        },
        onDone: (cleanText) => {
          if (streamingMsgIdRef.current !== myMsgId) return;
          const current = messagesRef.current;
          const finalText = cleanText || (current.find((m) => m.id === myMsgId)?.content ?? "");
          const extracted = extractPythonCodeBlock(finalText) ?? pendingCodeUpdateRef.current;
          pendingCodeUpdateRef.current = null;
          const displayContent = stripCodeFenceForDisplay(finalText);
          const next = current.map((m) =>
            m.id === myMsgId
              ? {
                  ...m,
                  content: displayContent,
                  isStreaming: false,
                  extractedCode: extracted ?? undefined,
                }
              : m
          );
          messagesRef.current = next;
          onMessagesChange(next);
          streamingMsgIdRef.current = null;
          setIsStreaming(false);
          resetThinkingIndicator();
        },
        onUpstreamError: (upstream) => {
          // Structured upstream-provider failure (overload, rate-limit,
          // timeout, etc.). Replace the empty/partial streaming placeholder
          // with a finalized message carrying the structured error payload —
          // <UpstreamErrorBubble> below renders it as a soft warning surface
          // naming the model + provider. We flip `marieUpstreamErrorSeenRef`
          // so the legacy `onError` callback that fires immediately after
          // doesn't double-write a generic "Error: ..." bubble on top.
          if (streamingMsgIdRef.current !== myMsgId) return;
          marieUpstreamErrorSeenRef.current = myMsgId;
          pendingCodeUpdateRef.current = null;
          const next = messagesRef.current.map((m) =>
            m.id === myMsgId
              ? {
                  ...m,
                  content: "",
                  isStreaming: false,
                  upstreamError: upstream,
                }
              : m,
          );
          messagesRef.current = next;
          onMessagesChange(next);
          streamingMsgIdRef.current = null;
          setIsStreaming(false);
          resetThinkingIndicator();
        },
        onError: (message) => {
          if (streamingMsgIdRef.current !== myMsgId) return;
          // If onUpstreamError already finalized this message with a
          // structured bubble, suppress the legacy generic-error bubble.
          if (marieUpstreamErrorSeenRef.current === myMsgId) {
            marieUpstreamErrorSeenRef.current = null;
            return;
          }
          pendingCodeUpdateRef.current = null;
          const next = [
            ...messagesRef.current.filter((m) => m.id !== myMsgId),
            {
              id: uuid(),
              role: "assistant" as const,
              content: `Error: ${message}`,
              isError: true,
            },
          ];
          messagesRef.current = next;
          onMessagesChange(next);
          streamingMsgIdRef.current = null;
          setIsStreaming(false);
          resetThinkingIndicator();
        },
        onTierLimit: handleMarieTierLimit,
      },
      attach.imageFiles
    );
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  function handleNewChat() {
    onMessagesChange(PLACEHOLDER_MESSAGES);
    setInputValue("");
    // Arm a fresh Marie session for the next chat. Keep the local ref in
    // sync immediately so a fast second click before the parent re-render
    // still sends `new_session: true`.
    newSessionArmedRef.current = true;
    marieSessionIdRef.current = null;
    onArmNewSession?.();
    // Full reset (Greg, 2026-05-11): also wipe surrounding workspace
    // (editor content, selected image, loadedScriptId) when the parent
    // wired a handler. Without this the chat felt reset but Marie still
    // had editor + image context on the next turn, which surprised users.
    onClearWorkspace?.();
  }

  function handleQuickReply(text: string) {
    if (isStreaming) return;
    setInputValue(text);
    // Trigger send on the next tick so state has settled
    setTimeout(() => {
      const trimmed = text.trim();
      if (!trimmed) return;

      // Strip checkpoint rows — they're UI markers, not turns Marie should see.
      const history = messagesRef.current
        .filter((m): m is ChatMessage & { role: "user" | "assistant" } => m.role !== "checkpoint")
        .map((m) => ({ role: m.role, content: m.content }));
      const attach = buildImageAttachPair();
      if (!attachImageThisTurn) setAttachImageThisTurn(true);

      const assistantMsgId = uuid();
      const assistantMsg: ChatMessage = {
        id: assistantMsgId,
        role: "assistant",
        content: "",
        isStreaming: true,
        hadImageAttached: attach.willAttach,
      };
      const userMsg: ChatMessage = { id: uuid(), role: "user", content: trimmed };

      const myMsgId = assistantMsgId;
      streamingMsgIdRef.current = myMsgId;
      onMessagesChange([...messagesRef.current, userMsg, assistantMsg]);
      setInputValue("");
      setIsStreaming(true);
      resetThinkingIndicator();
      startThinkingTimer();

      const sessionPayload = consumeMarieSessionPayload();

      marieApi.chatStream(
        {
          message: trimmed,
          history,
          model: selectedModel,
          current_code: getEditorCode?.(),
          user_name: getStoredUser().fullName ?? undefined,
          image_id: getSelectedImageId?.() ?? null,
          input_image_id: attach.input_image_id,
          input_image_source: attach.input_image_source,
          ...sessionPayload,
        },
        {
          onMeta: (meta) => {
            if (streamingMsgIdRef.current !== myMsgId) return;
            handleMarieMeta(meta);
            const next = messagesRef.current.map((m) =>
              m.id === myMsgId ? { ...m, meta } : m,
            );
            messagesRef.current = next;
            onMessagesChange(next);
          },
          onToken: (token) => {
            if (streamingMsgIdRef.current !== myMsgId) return;
            const next = messagesRef.current.map((m) =>
              m.id === myMsgId ? { ...m, content: m.content + token } : m
            );
            // Sync the ref immediately so the next rapid onToken call sees the
            // freshly-accumulated content instead of a stale render snapshot.
            messagesRef.current = next;
            onMessagesChange(next);
            setLastTokenAt(Date.now());
            const updated = next.find((m) => m.id === myMsgId);
            if (updated && isCodeIncoming(updated.content)) {
              setExpectingCode(true);
            }
          },
          onCodeUpdate: (code) => {
            if (streamingMsgIdRef.current !== myMsgId) return;
            pendingCodeUpdateRef.current = code;
            setCodeUpdateReceived(true);
            if (onInjectCode) onInjectCode(code);
          },
          onDone: (cleanText) => {
            if (streamingMsgIdRef.current !== myMsgId) return;
            const current = messagesRef.current;
            const finalText = cleanText || (current.find((m) => m.id === myMsgId)?.content ?? "");
            const extracted = extractPythonCodeBlock(finalText) ?? pendingCodeUpdateRef.current;
            pendingCodeUpdateRef.current = null;
            const displayContent = stripCodeFenceForDisplay(finalText);
            const next = current.map((m) =>
              m.id === myMsgId
                ? {
                    ...m,
                    content: displayContent,
                    isStreaming: false,
                    extractedCode: extracted ?? undefined,
                  }
                : m
            );
            messagesRef.current = next;
            onMessagesChange(next);
            streamingMsgIdRef.current = null;
            setIsStreaming(false);
            resetThinkingIndicator();
          },
          onUpstreamError: (upstream) => {
            // See handleSend's onUpstreamError comment for the rationale.
            if (streamingMsgIdRef.current !== myMsgId) return;
            marieUpstreamErrorSeenRef.current = myMsgId;
            pendingCodeUpdateRef.current = null;
            const next = messagesRef.current.map((m) =>
              m.id === myMsgId
                ? {
                    ...m,
                    content: "",
                    isStreaming: false,
                    upstreamError: upstream,
                  }
                : m,
            );
            messagesRef.current = next;
            onMessagesChange(next);
            streamingMsgIdRef.current = null;
            setIsStreaming(false);
            resetThinkingIndicator();
          },
          onError: (message) => {
            if (streamingMsgIdRef.current !== myMsgId) return;
            if (marieUpstreamErrorSeenRef.current === myMsgId) {
              marieUpstreamErrorSeenRef.current = null;
              return;
            }
            pendingCodeUpdateRef.current = null;
            const next = [
              ...messagesRef.current.filter((m) => m.id !== myMsgId),
              {
                id: uuid(),
                role: "assistant" as const,
                content: `Error: ${message}`,
                isError: true,
              },
            ];
            messagesRef.current = next;
            onMessagesChange(next);
            streamingMsgIdRef.current = null;
            setIsStreaming(false);
            resetThinkingIndicator();
          },
          onTierLimit: handleMarieTierLimit,
        },
        attach.imageFiles
      );
    }, 0);
  }

  // ── Hero-state chip: load a starter script by title ───────────
  // Fetches the library, finds the script whose title matches exactly, and
  // hands it off to the app-wide onLoadScript. If the script isn't found
  // (e.g. admin removed it) or no loader is wired, falls back gracefully by
  // sending the title as a plain chat prompt so the click is never dead.
  async function handleLoadStarterScriptChip(title: string) {
    if (isStreaming || loadingScriptChipLabel) return;
    if (!onLoadScript) {
      handleQuickReply(title);
      return;
    }
    setLoadingScriptChipLabel(title);
    try {
      const data = await libraryScriptsApi.list();
      const allScripts = [
        ...data.categories.flatMap((c) => c.scripts),
        ...data.uncategorized,
      ];
      const script = allScripts.find((s) => s.title === title);
      if (script) {
        onLoadScript(script.code, script.default_image_id, null, script.title);
      } else {
        // Fallback — starter missing. Don't leave the user with a dead click.
        handleQuickReply(title);
      }
    } catch {
      handleQuickReply(title);
    } finally {
      setLoadingScriptChipLabel(null);
    }
  }

  // ── Auto error analysis ──────────────────────────────────────
  // Sends a message to Marie where the chat bubble shows a short label but the
  // API receives the full error context. Keeps the chat clean for the user.
  function handleSilentAnalysis(displayText: string, apiText: string) {
    if (isStreaming) return;

    const userMsg: ChatMessage = { id: uuid(), role: "user", content: displayText };
    const attach = buildImageAttachPair();
    if (!attachImageThisTurn) setAttachImageThisTurn(true);
    const assistantMsgId = uuid();
    const assistantMsg: ChatMessage = {
      id: assistantMsgId,
      role: "assistant",
      content: "",
      isStreaming: true,
      hadImageAttached: attach.willAttach,
    };
    // Strip checkpoint rows — they're UI markers, not turns Marie should see.
    const history = messagesRef.current
      .filter((m): m is ChatMessage & { role: "user" | "assistant" } => m.role !== "checkpoint")
      .map((m) => ({ role: m.role, content: m.content }));

    const myMsgId = assistantMsgId;
    streamingMsgIdRef.current = myMsgId;
    onMessagesChange([...messagesRef.current, userMsg, assistantMsg]);
    setIsStreaming(true);
    resetThinkingIndicator();
    startThinkingTimer();

    const sessionPayload = consumeMarieSessionPayload();

    marieApi.chatStream(
      {
        message: apiText,
        history,
        model: selectedModel,
        current_code: getEditorCode?.(),
        user_name: getStoredUser().fullName ?? undefined,
        image_id: getSelectedImageId?.() ?? null,
        input_image_id: attach.input_image_id,
        input_image_source: attach.input_image_source,
        ...sessionPayload,
      },
      {
        onMeta: (meta) => {
          if (streamingMsgIdRef.current !== myMsgId) return;
          handleMarieMeta(meta);
          const next = messagesRef.current.map((m) =>
            m.id === myMsgId ? { ...m, meta } : m,
          );
          messagesRef.current = next;
          onMessagesChange(next);
        },
        onToken: (token) => {
          if (streamingMsgIdRef.current !== myMsgId) return;
          const next = messagesRef.current.map((m) =>
            m.id === myMsgId ? { ...m, content: m.content + token } : m
          );
          // Sync the ref immediately so the next rapid onToken call sees the
          // freshly-accumulated content instead of a stale render snapshot.
          messagesRef.current = next;
          onMessagesChange(next);
          setLastTokenAt(Date.now());
          const updated = next.find((m) => m.id === myMsgId);
          if (updated && isCodeIncoming(updated.content)) {
            setExpectingCode(true);
          }
        },
        onCodeUpdate: (code) => {
          if (streamingMsgIdRef.current !== myMsgId) return;
          pendingCodeUpdateRef.current = code;
          setCodeUpdateReceived(true);
          if (onInjectCode) onInjectCode(code);
        },
        onDone: (cleanText) => {
          if (streamingMsgIdRef.current !== myMsgId) return;
          const current = messagesRef.current;
          const finalText = cleanText || (current.find((m) => m.id === myMsgId)?.content ?? "");
          const extracted = extractPythonCodeBlock(finalText) ?? pendingCodeUpdateRef.current;
          pendingCodeUpdateRef.current = null;
          const displayContent = stripCodeFenceForDisplay(finalText);
          const next = current.map((m) =>
            m.id === myMsgId
              ? { ...m, content: displayContent, isStreaming: false, extractedCode: extracted ?? undefined }
              : m
          );
          messagesRef.current = next;
          onMessagesChange(next);
          streamingMsgIdRef.current = null;
          setIsStreaming(false);
          resetThinkingIndicator();
        },
        onUpstreamError: (upstream) => {
          // See handleSend's onUpstreamError comment for the rationale.
          if (streamingMsgIdRef.current !== myMsgId) return;
          marieUpstreamErrorSeenRef.current = myMsgId;
          pendingCodeUpdateRef.current = null;
          const next = messagesRef.current.map((m) =>
            m.id === myMsgId
              ? { ...m, content: "", isStreaming: false, upstreamError: upstream }
              : m,
          );
          messagesRef.current = next;
          onMessagesChange(next);
          streamingMsgIdRef.current = null;
          setIsStreaming(false);
          resetThinkingIndicator();
        },
        onError: (message) => {
          if (streamingMsgIdRef.current !== myMsgId) return;
          if (marieUpstreamErrorSeenRef.current === myMsgId) {
            marieUpstreamErrorSeenRef.current = null;
            return;
          }
          pendingCodeUpdateRef.current = null;
          const next = [
            ...messagesRef.current.filter((m) => m.id !== myMsgId),
            { id: uuid(), role: "assistant" as const, content: `Error: ${message}`, isError: true },
          ];
          messagesRef.current = next;
          onMessagesChange(next);
          streamingMsgIdRef.current = null;
          setIsStreaming(false);
          resetThinkingIndicator();
        },
        onTierLimit: handleMarieTierLimit,
      },
      attach.imageFiles
    );
  }

  // ── Proactive auto-image-analysis (Mia, 2026-06-05) ──────────
  // When the user actively picks a library image or uploads a temp image for
  // the editor input, Marie posts a short unprompted description. Modeled on
  // handleSilentAnalysis but with two key differences:
  //   • NO synthetic user bubble — only Marie's reply is rendered. (Locked
  //     decision: the analysis appears as a proactive Marie message.)
  //   • Sends auto_image_analysis: true; the backend withholds the code tool
  //     so no code_update ever arrives (onCodeUpdate is a no-op safety net).
  // Guards: skips while a turn is in flight (never clobbers an in-flight
  // stream); always force-attaches the image for this turn (ignores any
  // user-dismissed per-turn attach toggle, since the whole point is to
  // describe THIS image); reuses the live marie_session_id (never arms a new
  // session). Degrades quietly if no image is attachable.
  function handleSilentImageAnalysis() {
    if (isStreaming) return;

    // Force-attach the just-selected image regardless of the per-turn toggle —
    // we're describing this specific image. Re-arm the toggle UI for parity
    // with the other call sites (so the chip reflects the attached state).
    if (!attachImageThisTurn) setAttachImageThisTurn(true);
    // buildImageAttachPair reads attachImageThisTurn from this render's closure,
    // which may be false if the user had dismissed it — so build the attach
    // payload directly from the live getters instead of relying on the toggle.
    const libId = getSelectedImageId?.() ?? null;
    const libSource = getSelectedImageSource?.() ?? null;
    const tempFile = getTempImageFile?.() ?? null;

    let input_image_id: number | undefined;
    let input_image_source: "user" | "library" | undefined;
    const imageFiles: File[] = [];
    let willAttach = false;
    if (tempFile) {
      imageFiles.push(tempFile);
      willAttach = true;
    } else if (libId != null && libSource) {
      input_image_id = libId;
      input_image_source = libSource;
      willAttach = true;
    }
    // No attachable image (e.g. selection cleared between trigger and fire) —
    // degrade quietly, never post an empty/broken bubble.
    if (!willAttach) return;

    // Immediate, synchronous acknowledgment (Mia, 2026-06-05). Rendered as its
    // OWN completed Marie bubble the instant the trigger fires — BEFORE the
    // network request — so the user never sees unexplained "…" dots while Marie
    // thinks. The streamed description follows in the next bubble below.
    // This ack is the ONLY "let me take a look" line: the backend prompt no
    // longer opens with one (Sam removed it), so there's no double-post.
    // Frontend-only UI: it is NOT sent to the backend (not in `history`, not a
    // turn the model sees) and is not persisted to the marie_session — purely
    // a presentational reassurance bubble. That's fine: it carries no content
    // the model or session needs, and re-derives on every fresh analysis.
    const ackMsgId = uuid();
    const ackMsg: ChatMessage = {
      id: ackMsgId,
      role: "assistant",
      content: pickImageAnalysisAck(),
    };

    const assistantMsgId = uuid();
    const assistantMsg: ChatMessage = {
      id: assistantMsgId,
      role: "assistant",
      content: "",
      isStreaming: true,
      hadImageAttached: true,
    };
    // Strip checkpoint rows — UI markers, not turns Marie should see. Built
    // BEFORE we append the ack/placeholder so neither leaks into model history.
    const history = messagesRef.current
      .filter((m): m is ChatMessage & { role: "user" | "assistant" } => m.role !== "checkpoint")
      .map((m) => ({ role: m.role, content: m.content }));

    const myMsgId = assistantMsgId;
    streamingMsgIdRef.current = myMsgId;
    // NO user bubble — append the immediate ack bubble, then the streaming
    // placeholder that the description will fill in.
    onMessagesChange([...messagesRef.current, ackMsg, assistantMsg]);
    setIsStreaming(true);
    resetThinkingIndicator();
    startThinkingTimer();

    const sessionPayload = consumeMarieSessionPayload();

    marieApi.chatStream(
      {
        // Throwaway message — backend persists a clean "[auto] image analysis"
        // sentinel for the user turn, so this text never leaks into history.
        message: "Briefly describe this image.",
        history,
        model: selectedModel,
        current_code: getEditorCode?.(),
        user_name: getStoredUser().fullName ?? undefined,
        image_id: libId,
        input_image_id,
        input_image_source,
        auto_image_analysis: true,
        ...sessionPayload,
      },
      {
        onMeta: (meta) => {
          if (streamingMsgIdRef.current !== myMsgId) return;
          handleMarieMeta(meta);
          const next = messagesRef.current.map((m) =>
            m.id === myMsgId ? { ...m, meta } : m,
          );
          messagesRef.current = next;
          onMessagesChange(next);
        },
        onToken: (token) => {
          if (streamingMsgIdRef.current !== myMsgId) return;
          const next = messagesRef.current.map((m) =>
            m.id === myMsgId ? { ...m, content: m.content + token } : m
          );
          messagesRef.current = next;
          onMessagesChange(next);
          setLastTokenAt(Date.now());
        },
        onCodeUpdate: () => {
          // No code_update is ever emitted for auto_image_analysis turns
          // (backend withholds the code tool). Intentional no-op safety net.
        },
        onDone: (cleanText) => {
          if (streamingMsgIdRef.current !== myMsgId) return;
          const current = messagesRef.current;
          const finalText = cleanText || (current.find((m) => m.id === myMsgId)?.content ?? "");
          const displayContent = stripCodeFenceForDisplay(finalText);
          const next = current.map((m) =>
            m.id === myMsgId
              ? { ...m, content: displayContent, isStreaming: false }
              : m
          );
          messagesRef.current = next;
          onMessagesChange(next);
          streamingMsgIdRef.current = null;
          setIsStreaming(false);
          resetThinkingIndicator();
        },
        onUpstreamError: (upstream) => {
          if (streamingMsgIdRef.current !== myMsgId) return;
          marieUpstreamErrorSeenRef.current = myMsgId;
          const next = messagesRef.current.map((m) =>
            m.id === myMsgId
              ? { ...m, content: "", isStreaming: false, upstreamError: upstream }
              : m,
          );
          messagesRef.current = next;
          onMessagesChange(next);
          streamingMsgIdRef.current = null;
          setIsStreaming(false);
          resetThinkingIndicator();
        },
        onError: (message) => {
          if (streamingMsgIdRef.current !== myMsgId) return;
          if (marieUpstreamErrorSeenRef.current === myMsgId) {
            marieUpstreamErrorSeenRef.current = null;
            return;
          }
          // Auto-analysis is best-effort and unsolicited — don't dump a red
          // "Error: …" bubble on the user for a proactive turn they never
          // asked for. Drop BOTH the placeholder assistant message AND the
          // immediate ack bubble (a lone "Let me take a look…" with no
          // follow-up description would read as a broken/abandoned turn).
          const next = messagesRef.current.filter(
            (m) => m.id !== myMsgId && m.id !== ackMsgId,
          );
          messagesRef.current = next;
          onMessagesChange(next);
          streamingMsgIdRef.current = null;
          setIsStreaming(false);
          resetThinkingIndicator();
        },
        onTierLimit: handleMarieTierLimit,
      },
      imageFiles
    );
  }

  // When the parent signals a script failure, fire error analysis into Marie.
  // The prev-value ref prevents re-sending on re-renders before the parent resets the prop.
  const prevAutoAnalyzeRef = useRef<string | null>(null);
  useEffect(() => {
    if (!autoAnalyzeError || autoAnalyzeError === prevAutoAnalyzeRef.current) return;
    prevAutoAnalyzeRef.current = autoAnalyzeError;
    onAutoAnalyzeErrorConsumed?.();
    handleSilentAnalysis("Script failed — analysing…", autoAnalyzeError);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoAnalyzeError]);

  // Fire proactive image analysis when the parent bumps autoAnalyzeImageKey.
  // Dedupe on BOTH the key (so a re-render doesn't re-fire) AND the image
  // identity (so re-selecting the SAME image — even via a fresh key bump —
  // is a no-op; only a genuinely different image re-fires). The in-flight
  // guard lives inside handleSilentImageAnalysis (isStreaming check).
  const prevAutoAnalyzeImageKeyRef = useRef<number | undefined>(autoAnalyzeImageKey);
  const lastAnalyzedImageIdentityRef = useRef<string | null>(null);
  useEffect(() => {
    if (autoAnalyzeImageKey === undefined) return;
    if (autoAnalyzeImageKey === prevAutoAnalyzeImageKeyRef.current) return;
    prevAutoAnalyzeImageKeyRef.current = autoAnalyzeImageKey;
    const identity = autoAnalyzeImageIdentity ?? null;
    if (!identity) return;
    if (identity === lastAnalyzedImageIdentityRef.current) return;
    lastAnalyzedImageIdentityRef.current = identity;
    handleSilentImageAnalysis();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoAnalyzeImageKey]);

  const prevPendingPromptRef = useRef<string | null>(null);
  const prevIsHeroRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (!pendingPrompt || pendingPrompt === prevPendingPromptRef.current) return;
    prevPendingPromptRef.current = pendingPrompt;
    onPendingPromptConsumed?.();
    handleQuickReply(pendingPrompt);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingPrompt]);

  // ── Streaming simulation ─────────────────────────────────────
  function handleSimulateCodeResponse() {
    if (isStreaming) return;

    const streamingMsgId = uuid();
    // Add the AI message with an empty streaming code block
    const newMsg: ChatMessage = {
      id: streamingMsgId,
      role: "assistant",
      content: DEMO_CODE_INTRO,
      streamingCode: "",
      isStreaming: true,
    };
    onMessagesChange([...messages, newMsg]);
    setIsStreaming(true);

    let charIndex = 0;
    const totalChars = DEMO_CODE_SNIPPET.length;

    streamIntervalRef.current = setInterval(() => {
      charIndex += 1;
      const partial = DEMO_CODE_SNIPPET.slice(0, charIndex);
      const done = charIndex >= totalChars;

      // Update the streaming message in place using the ref so the interval
      // always reads the current messages array without stale closure issues.
      const updated = messagesRef.current.map((m) =>
        m.id === streamingMsgId
          ? { ...m, streamingCode: partial, isStreaming: !done }
          : m
      );
      onMessagesChange(updated);

      if (done) {
        if (streamIntervalRef.current !== null) {
          clearInterval(streamIntervalRef.current);
          streamIntervalRef.current = null;
        }
        setIsStreaming(false);
        // Notify parent so it can write into the editor and set the dot
        onCodeStreamed?.(DEMO_CODE_SNIPPET);
        // Always auto-inject extracted code into the editor
        onInjectCode?.(DEMO_CODE_SNIPPET);
        onMessagesChange(
          messagesRef.current.map((m) =>
            m.id === streamingMsgId
              ? {
                  ...m,
                  extractedCode: DEMO_CODE_SNIPPET,
                }
              : m
          )
        );
      }
    }, STREAM_CHAR_INTERVAL_MS);
  }

  // ── Render helpers ───────────────────────────────────────────

  // Returns true when the text has opened a code fence (``` present) but
  // has not yet closed it. We count triple-backtick occurrences: an odd
  // count means one fence is still open.
  function isInsideCodeFence(text: string): boolean {
    const matches = text.match(/```/g);
    if (!matches) return false;
    return matches.length % 2 !== 0;
  }

  function renderMessageBubble(msg: ChatMessage) {
    // ── Checkpoint row (system event, not a chat turn) ───────────────
    // Dropped into the messages list by handleConfirmRestore after a
    // successful Restore. Renders as a centered hairline divider with
    // compact metadata text and an inline Run chip — visually distinct
    // from a chat bubble so it reads as "this happened" rather than
    // "Marie said this". Multiple consecutive restores produce multiple
    // checkpoint rows in chronological order. They carry no extractedCode
    // and are deliberately ignored by the activeCodeMsgId auto-follow
    // effect, so they never become "current".
    if (msg.role === "checkpoint") {
      return (
        <Box
          key={msg.id}
          role="status"
          aria-label={msg.content}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            my: 0.5,
            // Hairline rules above and below the row, theme-aware via
            // the `divider` token so light + dark both render correctly.
            borderTop: "1px solid",
            borderBottom: "1px solid",
            borderColor: "divider",
            py: 0.75,
            px: 1,
          }}
        >
          <RestoreOutlinedIcon sx={{ fontSize: 14, color: "text.secondary", flexShrink: 0 }} />
          <Typography
            variant="caption"
            sx={{
              flex: 1,
              fontSize: "0.75rem",
              color: "text.secondary",
              fontStyle: "italic",
              // Center-ish: text takes flex:1 so the Run chip pins to the
              // right edge while the icon+label sit left-of-center. Reads
              // as a timeline marker, not a left-aligned message.
              textAlign: "left",
            }}
          >
            {msg.content}
          </Typography>
          <Chip
            icon={isRunning ? <CircularProgress size={11} color="inherit" /> : <PlayArrowOutlinedIcon sx={{ fontSize: 13 }} />}
            label={isRunning ? "Running…" : "Run"}
            size="small"
            color="primary"
            variant="outlined"
            clickable={!isRunning}
            disabled={isRunning}
            onClick={() => { if (!isRunning) onRunScript?.(); }}
            sx={{ fontSize: "0.6875rem", height: 22, "& .MuiChip-icon": { ml: 0.5 }, flexShrink: 0 }}
          />
        </Box>
      );
    }

    // ── Structured upstream-error bubble (Sam, 2026-05-14) ───────────
    // The backend emits `{"type": "error", "error": <UpstreamError>}`
    // on the SSE stream when an upstream AI provider returns a
    // transient/classifiable failure (overload, rate-limit, timeout,
    // mid-stream truncation, etc.). The chatStream callbacks store
    // the payload on `msg.upstreamError`, and we render it here as
    // a soft warning surface — naming the model + provider so it
    // reads as "Anthropic is overloaded" rather than "Maps Script
    // Helper crashed". Avatar/scaffold mirrors a normal assistant
    // row so the failed turn still sits in the chat timeline.
    if (msg.upstreamError) {
      return (
        <Box
          key={msg.id}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 0.75,
              maxWidth: "90%",
            }}
          >
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                bgcolor: "secondary.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                mt: 0.25,
              }}
            >
              <MarieIcon fontSize={18} color="rgba(0,0,0,0.87)" />
            </Box>
            <Box sx={{ maxWidth: "100%", minWidth: 0 }}>
              <UpstreamErrorBubble error={msg.upstreamError} />
            </Box>
          </Box>
        </Box>
      );
    }

    return (
      <Box
        key={msg.id}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: msg.role === "user" ? "flex-end" : "flex-start",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 0.75,
            flexDirection: msg.role === "user" ? "row-reverse" : "row",
            maxWidth: "90%",
          }}
        >
          {/* Avatar dot */}
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              bgcolor: msg.role === "user" ? "primary.dark" : "secondary.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              mt: 0.25,
            }}
          >
            {msg.role === "user" ? (
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  color: "primary.contrastText",
                  lineHeight: 1,
                  letterSpacing: "0.02em",
                }}
              >
                {getUserInitials()}
              </Typography>
            ) : (
              <MarieIcon fontSize={18} color="rgba(0,0,0,0.87)" />
            )}
          </Box>

          {/* Bubble */}
          <Box
            sx={{
              px: 1.25,
              py: 0.75,
              borderRadius: 1,
              bgcolor: msg.role === "user" ? "action.selected" : "background.default",
              border: "1px solid",
              borderColor: "divider",
              maxWidth: "100%",
              minWidth: 0,
            }}
          >
            {/* ── Vision-attach meta annotation ───────────────────────
                Small captions at the top of Marie's bubble that warn
                when the backend gracefully degraded to text-only
                despite an image being selected, and per-attachment
                warnings for individual uploads that failed validation.
                Quiet by design — never a separate bubble, never on the
                normal happy path.
            */}
            {msg.role === "assistant" && msg.meta && (
              <>
                {!msg.meta.image_attached && msg.hadImageAttached && (
                  <Tooltip
                    title="Marie couldn't read the image bytes for this turn — likely the file isn't reachable on the server, or no vision-capable model is available. Your message went through as text only."
                    placement="top"
                    arrow
                  >
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                        mb: 0.5,
                        color: "warning.main",
                      }}
                    >
                      <ErrorOutlineIcon sx={{ fontSize: 12 }} />
                      <Typography
                        variant="caption"
                        sx={{
                          fontStyle: "italic",
                          fontSize: "0.6875rem",
                          color: "warning.main",
                        }}
                      >
                        Couldn't attach image — text only this turn
                      </Typography>
                    </Box>
                  </Tooltip>
                )}
                {/* ── Per-attachment warnings (2026-05-07 multipart) ───
                    Backend surfaces a string per individual upload that
                    failed validation (oversize, bad MIME, decode failure,
                    count cap exceeded, etc.). The turn still completed
                    with whatever images survived; these tell the user
                    which ones were dropped and why. One line per warning,
                    italic-secondary styling. */}
                {msg.meta.warnings && msg.meta.warnings.length > 0 && (
                  <Box sx={{ display: "flex", flexDirection: "column", mb: 0.5 }}>
                    {msg.meta.warnings.map((w, i) => (
                      <Box
                        key={i}
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.5,
                          color: "text.secondary",
                        }}
                      >
                        <ErrorOutlineIcon sx={{ fontSize: 12 }} />
                        <Typography
                          variant="caption"
                          sx={{
                            fontStyle: "italic",
                            fontSize: "0.6875rem",
                            color: "text.secondary",
                          }}
                        >
                          {w}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </>
            )}

            {/* ── Typing indicator: shown while streaming before first token ── */}
            {msg.isStreaming && msg.content === "" && msg.streamingCode === undefined ? (
              <Box
                aria-label="Marie is typing"
                sx={{ display: "flex", alignItems: "center", gap: "5px", py: 0.25 }}
              >
                {[0, 1, 2].map((i) => (
                  <Box
                    key={i}
                    aria-hidden
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      bgcolor: "text.secondary",
                      "@keyframes marieTypingBounce": {
                        "0%, 80%, 100%": { transform: "translateY(0)" },
                        "40%": { transform: "translateY(-5px)" },
                      },
                      animation: `marieTypingBounce 1.2s ease-in-out ${i * 150}ms infinite`,
                    }}
                  />
                ))}
              </Box>
            ) : msg.role === "assistant" ? (
              // Assistant messages render through MarkdownChatBody so
              // bold/italic/lists/headings format properly. We strip
              // any code fence first — Marie's code routes to the
              // editor and partial fences during streaming would
              // render as broken code blocks otherwise.
              //
              // Streaming cursor: shown unless a code fence is
              // currently open (the "Generating code…" card below
              // already signals activity) and unless the demo
              // streamingCode path is in use (it has its own cursor).
              <MarkdownChatBody
                content={stripCodeFenceForDisplay(msg.content)}
                isStreaming={
                  msg.isStreaming === true &&
                  msg.streamingCode === undefined &&
                  !isInsideCodeFence(msg.content)
                }
                cursorColor="primary.main"
              />
            ) : (
              // User messages stay as plain text — the user didn't
              // mean for asterisks or underscores in their question
              // to format anything.
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {msg.content}
              </Typography>
            )}

            {/* ── Code generating card: shown while a code fence is open mid-stream ── */}
            {msg.isStreaming && msg.streamingCode === undefined && isInsideCodeFence(msg.content) && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  mt: stripCodeFenceForDisplay(msg.content).length > 0 ? 1 : 0,
                  px: 1.25,
                  py: 0.75,
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: "action.hover",
                }}
              >
                <CodeOutlinedIcon sx={{ fontSize: 13, color: "text.secondary", flexShrink: 0 }} />
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1, fontSize: "0.6875rem" }}>
                  Generating code…
                </Typography>
                <CircularProgress size={10} thickness={5} sx={{ color: "text.secondary", flexShrink: 0 }} />
              </Box>
            )}

            {/* Inline streaming / static code block */}
            {(msg.streamingCode !== undefined) && (
              <Box
                sx={{
                  mt: 1,
                  borderRadius: "6px",
                  bgcolor: "background.paper",
                  border: "1px solid",
                  borderColor: "divider",
                  overflow: "hidden",
                }}
              >
                {/* Code block header */}
                <Box
                  sx={{
                    px: 1.25,
                    py: 0.4,
                    bgcolor: "action.hover",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                  }}
                >
                  <CodeOutlinedIcon sx={{ fontSize: 12, color: "text.secondary" }} />
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.6875rem" }}>
                    python
                  </Typography>
                  {msg.isStreaming && (
                    <Box
                      sx={{
                        ml: "auto",
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: "primary.main",
                        animation: "pulse 1s ease-in-out infinite",
                        "@keyframes pulse": {
                          "0%, 100%": { opacity: 1 },
                          "50%": { opacity: 0.3 },
                        },
                      }}
                    />
                  )}
                </Box>

                {/* Code content */}
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    px: 1.25,
                    py: 1,
                    fontFamily: '"Cascadia Code", "Fira Code", "Consolas", monospace',
                    fontSize: "0.75rem",
                    lineHeight: 1.6,
                    color: "text.primary",
                    whiteSpace: "pre",
                    overflowX: "auto",
                    // Blinking cursor while streaming
                    "&::after": msg.isStreaming
                      ? {
                          content: '"▌"',
                          color: "primary.main",
                          animation: "blink 0.7s step-end infinite",
                          "@keyframes blink": {
                            "0%, 100%": { opacity: 1 },
                            "50%": { opacity: 0 },
                          },
                        }
                      : {},
                  }}
                >
                  {msg.streamingCode}
                </Box>
              </Box>
            )}

            {/* Inline action chips — appear after a completed demo code block */}
            {msg.streamingCode !== undefined && !msg.isStreaming && (
              <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mt: 1, alignItems: "center" }}>
                <Chip
                  icon={isRunning ? <CircularProgress size={11} color="inherit" /> : <PlayArrowOutlinedIcon sx={{ fontSize: 13 }} />}
                  label={isRunning ? "Running…" : "Run Script"}
                  size="small"
                  color="primary"
                  variant="outlined"
                  clickable={!isRunning}
                  disabled={isRunning}
                  onClick={() => { if (!isRunning) onRunScript?.(); }}
                  sx={{ fontSize: "0.6875rem", height: 24, "& .MuiChip-icon": { ml: 0.5 } }}
                />
                {onViewCode && (
                  <Chip
                    icon={<CodeOutlinedIcon sx={{ fontSize: 13 }} />}
                    label="View Code"
                    size="small"
                    variant="outlined"
                    clickable
                    onClick={onViewCode}
                    sx={{ fontSize: "0.6875rem", height: 24, "& .MuiChip-icon": { ml: 0.5 } }}
                  />
                )}
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.6875rem" }}>
                  ↑ Code updated
                </Typography>
              </Box>
            )}

            {/* When a code block was extracted, show "↑ Code updated" caption + Run Script chip.
                The code goes silently into the Monaco editor — it never renders in the bubble.
                If the entire message was just a code block (no surrounding text), the
                Typography above renders empty string, so this caption is the only content.

                Active-version chips (Mia, 2026-05-11):
                  • The message whose code is currently in the editor (msg.id === activeCodeMsgId)
                    gets a non-clickable "Current" chip (state, not action).
                  • Every OTHER code-emitting message gets a clickable "Restore" chip.
                  • Symmetric — user can ping-pong freely between any prior versions. */}
            {msg.extractedCode !== undefined && !msg.isStreaming && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.75, flexWrap: "wrap" }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.6875rem", fontStyle: "italic" }}>
                  ↑ Code updated
                </Typography>
                <Chip
                  icon={isRunning ? <CircularProgress size={11} color="inherit" /> : <PlayArrowOutlinedIcon sx={{ fontSize: 13 }} />}
                  label={isRunning ? "Running…" : "Run Script"}
                  size="small"
                  color="primary"
                  variant="outlined"
                  clickable={!isRunning}
                  disabled={isRunning}
                  onClick={() => { if (!isRunning) onRunScript?.(); }}
                  sx={{ fontSize: "0.6875rem", height: 24, "& .MuiChip-icon": { ml: 0.5 } }}
                />
                {onRestoreCode && msg.id === activeCodeMsgId && (
                  <Tooltip title="This version is currently in the editor" arrow>
                    {/* Non-clickable state chip. Wrapped in <span> so the Tooltip
                        still receives pointer events on a disabled-feeling chip. */}
                    <span>
                      <Chip
                        icon={<CheckCircleOutlineIcon sx={{ fontSize: 13 }} />}
                        label="Current"
                        size="small"
                        color="success"
                        variant="outlined"
                        sx={{
                          fontSize: "0.6875rem",
                          height: 24,
                          "& .MuiChip-icon": { ml: 0.5, color: "success.main" },
                          // Reads as state, not action — no hover lift, default cursor.
                          cursor: "default",
                        }}
                      />
                    </span>
                  </Tooltip>
                )}
                {onRestoreCode && msg.id !== activeCodeMsgId && (
                  <Tooltip title="Restore this version to the editor" arrow>
                    <Chip
                      icon={
                        restoredFlashMsgId === msg.id
                          ? <CheckCircleOutlineIcon sx={{ fontSize: 13 }} />
                          : <RestoreOutlinedIcon sx={{ fontSize: 13 }} />
                      }
                      label={restoredFlashMsgId === msg.id ? "Restored" : "Restore"}
                      size="small"
                      color={restoredFlashMsgId === msg.id ? "success" : "secondary"}
                      variant="outlined"
                      clickable
                      onClick={() => handleRestoreClick(msg)}
                      sx={{ fontSize: "0.6875rem", height: 24, "& .MuiChip-icon": { ml: 0.5 } }}
                    />
                  </Tooltip>
                )}
              </Box>
            )}

            {/* Clarifying question buttons — Q1 (Tile Set / Image Layer) and Q2 (Single-tile / Batch) */}
            {msg.role === "assistant" && !msg.isStreaming && !msg.extractedCode && !msg.isError && (() => {
              const opts = detectClarifyingButtons(msg.content);
              if (!opts) return null;
              return (
                <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mt: 1 }}>
                  {opts.map((opt) => (
                    <Button
                      key={opt.reply}
                      size="small"
                      variant="outlined"
                      disabled={isStreaming}
                      onClick={() => handleQuickReply(opt.reply)}
                      sx={{ fontSize: "0.6875rem", py: 0.25, px: 1, minHeight: 24 }}
                    >
                      {opt.label}
                    </Button>
                  ))}
                </Box>
              );
            })()}

            {/* Inline action chips — appear after an execution failure message */}
            {msg.isError && (
              <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mt: 1 }}>
                <Chip
                  icon={<AutoFixHighOutlinedIcon sx={{ fontSize: 13 }} />}
                  label="Fix It"
                  size="small"
                  color="warning"
                  variant="outlined"
                  clickable
                  onClick={() => {
                    console.log("[Marie] Fix It clicked");
                    onFixIt?.();
                  }}
                  sx={{ fontSize: "0.6875rem", height: 24, "& .MuiChip-icon": { ml: 0.5 } }}
                />
                <Chip
                  icon={<ErrorOutlineIcon sx={{ fontSize: 13 }} />}
                  label="View Error Details"
                  size="small"
                  variant="outlined"
                  clickable
                  onClick={() => {
                    console.log("[Marie] View Error Details clicked");
                  }}
                  sx={{ fontSize: "0.6875rem", height: 24, "& .MuiChip-icon": { ml: 0.5 } }}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    );
  }

  // ── Layout ───────────────────────────────────────────────────
  const isHeroState = fullWidth && messages.length === 1 && messages[0].id === "1";

  useEffect(() => {
    if (prevIsHeroRef.current === null) {
      prevIsHeroRef.current = isHeroState;
      return;
    }
    if (prevIsHeroRef.current && !isHeroState) {
      setInputSlideIn(true);
      const t = setTimeout(() => setInputSlideIn(false), 1000);
      prevIsHeroRef.current = false;
      return () => clearTimeout(t);
    }
    prevIsHeroRef.current = isHeroState;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHeroState]);

  // (Active-version tracking lives in `activeCodeMsgId` state, set/updated
  // by the auto-follow useEffect on `messages` change and by handleConfirmRestore.
  // Replaces the pre-2026-05-11 derivation that suppressed Restore on the
  // latest message — that broke the moment a user restored an older version,
  // because the "latest" message was no longer what was in the editor.)

  function handleRestoreClick(msg: ChatMessage) {
    if (!msg.extractedCode) return;
    setPendingRestore({ code: msg.extractedCode, msgId: msg.id });
  }

  function handleConfirmRestore() {
    if (!pendingRestore) return;
    const { code, msgId } = pendingRestore;
    onRestoreCode?.(code);
    setPendingRestore(null);
    setRestoreSnackbarOpen(true);
    setRestoredFlashMsgId(msgId);
    // Mark the restored message as the active version. From here on, IT shows
    // the "Current" indicator, and every other code-emitting message (including
    // the one that used to be active) gets a Restore chip — so the user can
    // ping-pong back to where they came from.
    setActiveCodeMsgId(msgId);
    // Append a checkpoint row at the bottom of the chat so the user has a
    // grounding "now" anchor near the chat input after restoring an older
    // version. Without this, restoring a message far up the scroll leaves
    // the "Current" chip stranded above the fold and the chat feels lost.
    // Checkpoints carry no extractedCode → ignored by the auto-follow
    // effect, never get a Restore chip, never become "active".
    const checkpointMsg: ChatMessage = {
      id: uuid(),
      role: "checkpoint",
      content: "Restored Marie's earlier version",
    };
    onMessagesChange([...messagesRef.current, checkpointMsg]);
    // Drop the inline "Restored ✓" pulse after ~1.2s — long enough to read,
    // short enough that the chip returns to its restorable state quickly
    // in case the user wants to revert their revert.
    setTimeout(() => {
      setRestoredFlashMsgId((current) => (current === msgId ? null : current));
    }, 1200);
  }

  const panelContent = (
    <>
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.paper",
        borderLeft: fullWidth ? "none" : "1px solid",
        borderColor: "divider",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 1.5,
          py: 0.75,
          borderBottom: "1px solid",
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          flexShrink: 0,
          minHeight: 44,
        }}
      >
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: "50%",
            bgcolor: "secondary.main",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <MarieIcon fontSize={18} color="rgba(0,0,0,0.87)" />
        </Box>
        <Typography variant="subtitle2" sx={{ flex: 1, minWidth: 0 }}>
          Marie
        </Typography>

        {/* Model selector — compact, inline in header.
            Restricted models (not in `allowedSet`) render disabled with a
            tooltip explaining advanced access — see `allowedSet` definition
            above. Tooltip wraps each row in a span (MUI Tooltip will not
            fire on a directly-disabled element). */}
        <Select
          value={selectedModel}
          onChange={(e) => onSelectedModelChange(e.target.value)}
          size="small"
          displayEmpty
          // Trigger button (closed state) shows the capability icon to the
          // LEFT of the selected model's label, so users see what tier of
          // model they're using at a glance — same icon + same neutral
          // colour treatment as the dropdown rows.
          renderValue={(value) => {
            const selected = AI_MODELS.find((m) => m.id === value);
            if (!selected) {
              return (
                <Typography
                  variant="body2"
                  sx={{ fontSize: "0.75rem" }}
                  component="span"
                >
                  {typeof value === "string" ? value : ""}
                </Typography>
              );
            }
            return (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  minWidth: 0,
                }}
              >
                <CapabilityIcon capability={selected.capability} fontSize={14} />
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.75rem",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    minWidth: 0,
                  }}
                  component="span"
                >
                  {selected.label}
                </Typography>
              </Box>
            );
          }}
          sx={{
            fontSize: "0.75rem",
            height: 28,
            flexShrink: 0,
            minWidth: 100,
            maxWidth: 130,
            "& .MuiSelect-select": { py: "3px", px: "8px" },
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "divider" },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                // Let the dropdown breathe wider than the 130px trigger so
                // the model label + lock icon don't get clipped.
                minWidth: 220,
                maxWidth: 280,
              },
            },
          }}
        >
          {AI_MODELS.map((m: AiModel) => {
            const isAllowed = allowedSet === null || allowedSet.has(m.id);
            // Capability icon sits LEFT of the label. Tooltip is bound to
            // the icon's own wrapper so only hovering the icon triggers it.
            // For disabled rows MUI sets `pointer-events: none` on the
            // MenuItem, which suppresses this inner tooltip entirely —
            // hovering a disabled row fires ONLY the outer "restricted"
            // tooltip wrapping the row, no stacking. For enabled rows the
            // outer wrapper isn't rendered so only the icon tooltip fires.
            const capabilityIcon = (
              <Tooltip
                title={CAPABILITY_TOOLTIP_COPY[m.capability]}
                placement="left"
                arrow
              >
                <Box
                  component="span"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    flexShrink: 0,
                  }}
                >
                  <CapabilityIcon capability={m.capability} fontSize={16} />
                </Box>
              </Tooltip>
            );
            const item = (
              <MenuItem
                key={m.id}
                value={m.id}
                disabled={!isAllowed}
                sx={{
                  // MUI's default disabled opacity (0.38) is too washed out
                  // here — labels become hard to read on the small font.
                  // 0.5 keeps them recognisable while still clearly distinct
                  // from the allowed rows. The capability icon inherits this
                  // opacity automatically since it lives inside the row.
                  "&.Mui-disabled": { opacity: 0.5 },
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                {capabilityIcon}
                <Typography
                  variant="body2"
                  sx={{ fontSize: "0.75rem", flex: 1, minWidth: 0 }}
                >
                  {m.label}
                </Typography>
                {!isAllowed && (
                  <LockOutlinedIcon
                    sx={{
                      fontSize: 14,
                      color: "text.secondary",
                      flexShrink: 0,
                    }}
                  />
                )}
              </MenuItem>
            );
            if (isAllowed) return item;
            return (
              <Tooltip
                key={m.id}
                title="Advanced reasoning models and higher-end models are reserved for advanced users. Please request advanced capability if you require it."
                placement="right"
                arrow
              >
                {/* span wrapper — Tooltip can't attach a listener directly
                    to a disabled MenuItem (pointer-events: none) */}
                <span style={{ display: "block" }}>{item}</span>
              </Tooltip>
            );
          })}
          {hasDisabledModels && (
            // Soft caption — anchors the dimmed rows so first-time basic
            // users have a non-tooltip cue before they hover. Not a
            // MenuItem (would be selectable / keyboardable); plain Box.
            <Box
              sx={{
                px: 1.5,
                pt: 0.75,
                pb: 0.5,
                borderTop: "1px solid",
                borderColor: "divider",
                pointerEvents: "none",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.6875rem",
                  color: "text.secondary",
                  fontStyle: "italic",
                  lineHeight: 1.3,
                  display: "block",
                }}
              >
                Advanced reasoning and higher-end models are reserved for advanced users. If you need access to these models, please request advanced capability from the Maps Discover Studio Administrator.
              </Typography>
            </Box>
          )}
        </Select>

        <Tooltip title="Start new chat">
          <IconButton size="small" onClick={handleNewChat} aria-label="New chat" sx={{ flexShrink: 0 }}>
            <AddCommentOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {(() => {
        const firstName = extractFirstName(getStoredUser().fullName);
        // kind="prompt"  → sends the label into the chat via handleQuickReply
        // kind="script"  → loads the matching starter script (by title) into the editor
        const suggestions: { label: string; kind: "prompt" | "script" }[] = [
          { label: "Write me a new script", kind: "prompt" },
          { label: "False Color — Single Image", kind: "script" },
          { label: "What kinds of scripts can I write?", kind: "prompt" },
          { label: "Explain the Maps API", kind: "prompt" },
        ];

        // ── "Image attached" chip ────────────────────────────────
        // Renders above the input whenever an image is selected — either a
        // library/user pick (currentImageId set) OR a "Upload temp image"
        // disk file (currentTempFile set). Two states:
        //   1. armed   → shows the filename + an X to detach for this turn
        //   2. detached → shows a greyed/collapsed "(image detached for this
        //                  turn — re-attach)" link that flips state back on
        // The X is per-turn only; the next message after sending re-arms,
        // and picking a new image always re-arms (handled in the effect
        // above). Greg explicitly asked for no persistent toggle.
        // For temp files we fall back to `currentTempFile.name` (the disk
        // filename) since they have no DB-backed display name.
        const hasImageSelection =
          (currentImageId != null && currentImageName != null) ||
          currentTempFile != null;
        const chipLabelName = currentImageName ?? currentTempFile?.name ?? null;
        const imageAttachChip = hasImageSelection ? (
          attachImageThisTurn ? (
            <Tooltip
              title="Marie can see this image when you chat. Click X to detach for this turn."
              placement="top"
              arrow
            >
              <Chip
                icon={<ImageOutlinedIcon sx={{ fontSize: 14 }} />}
                label={`${chipLabelName} attached`}
                variant="outlined"
                size="small"
                onDelete={() => setAttachImageThisTurn(false)}
                deleteIcon={
                  <CloseIcon
                    sx={{ fontSize: 14 }}
                    aria-label="Detach image for this turn"
                  />
                }
                sx={{
                  alignSelf: "flex-start",
                  mb: 0.75,
                  fontSize: "0.6875rem",
                  height: 24,
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
                  borderColor: "primary.light",
                  color: "primary.main",
                  "& .MuiChip-icon": { color: "primary.main", ml: 0.5 },
                  "& .MuiChip-deleteIcon": {
                    color: "primary.main",
                    "&:hover": { color: "primary.dark" },
                  },
                  "& .MuiChip-label": { px: 0.75 },
                }}
              />
            </Tooltip>
          ) : (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                mb: 0.75,
                alignSelf: "flex-start",
              }}
            >
              <Typography
                variant="caption"
                sx={{ fontSize: "0.6875rem", color: "text.disabled" }}
              >
                (image detached for this turn —{" "}
              </Typography>
              <Box
                component="button"
                type="button"
                onClick={() => setAttachImageThisTurn(true)}
                sx={{
                  background: "none",
                  border: "none",
                  p: 0,
                  fontSize: "0.6875rem",
                  fontFamily: "inherit",
                  color: "primary.main",
                  textDecoration: "underline",
                  cursor: "pointer",
                  "&:hover": { color: "primary.dark" },
                  "&:focus-visible": {
                    outline: "2px solid",
                    outlineColor: "primary.main",
                    outlineOffset: 2,
                    borderRadius: "2px",
                  },
                }}
              >
                re-attach
              </Box>
              <Typography
                variant="caption"
                sx={{ fontSize: "0.6875rem", color: "text.disabled" }}
              >
                )
              </Typography>
            </Box>
          )
        ) : null;

        // Inline hint shown above the input area when the basic-tier daily
        // Marie cap has been hit for this session (Sam, 2026-05-14). Stays
        // sticky until reload — the user already saw the Snackbar; this
        // is the persistent "why is the input greyed out?" affordance.
        const tierLimitHint = marieChatLocked ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              mb: 0.75,
              px: 1,
              py: 0.5,
              borderRadius: 0.5,
              bgcolor: (t) => alpha(t.palette.warning.main, 0.08),
              border: "1px solid",
              borderColor: "warning.light",
            }}
          >
            <ErrorOutlineIcon sx={{ fontSize: 14, color: "warning.main" }} />
            <Typography
              variant="caption"
              sx={{ fontSize: "0.6875rem", color: "text.secondary" }}
            >
              Daily Marie limit reached — contact an admin for full access.
            </Typography>
          </Box>
        ) : null;

        const inputContent = (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {tierLimitHint}
            {imageAttachChip}
            <Box sx={{ display: "flex", gap: 0.75, alignItems: "flex-end" }}>
              <TextField
                multiline
                maxRows={4}
                size="small"
                fullWidth
                placeholder={marieChatLocked ? "Daily Marie limit reached" : "Ask Marie..."}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={marieChatLocked}
                sx={{ "& .MuiOutlinedInput-root": { fontSize: "0.8125rem" } }}
              />
              <Tooltip title={marieChatLocked ? "Daily Marie limit reached" : "Send (Enter)"}>
                <span>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={handleSend}
                    disabled={!inputValue.trim() || isStreaming || marieChatLocked}
                    aria-label="Send message"
                    sx={{ mb: 0.25 }}
                  >
                    <SendOutlinedIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
        );

        return (
          <>
            {/* Middle area — hero greeting OR message history */}
            {isHeroState ? (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  px: 2,
                  overflow: "hidden",
                }}
              >
                <Box sx={{ width: "100%", maxWidth: 820 }}>
                  {/* Marie avatar */}
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: "50%",
                      bgcolor: "secondary.main",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 2.5,
                    }}
                  >
                    <MarieIcon fontSize={30} color="rgba(0,0,0,0.87)" />
                  </Box>

                  <Typography variant="h4" sx={{ color: "text.secondary", fontWeight: 400, mb: 0.5 }}>
                    {firstName ? `Hi ${firstName},` : "Hello,"}
                  </Typography>
                  <Typography variant="h2" sx={{ fontWeight: 600, mb: 3, lineHeight: 1.15 }}>
                    What would you like to script today?
                  </Typography>

                  {/* Suggestion chips */}
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "nowrap",
                      gap: 1,
                      mb: 3,
                      overflowX: "auto",
                      scrollbarWidth: "none",
                      "&::-webkit-scrollbar": { display: "none" },
                    }}
                  >
                    {suggestions.map((s) => {
                      const isLoading = loadingScriptChipLabel === s.label;
                      const handleClick = () => {
                        if (isLoading) return;
                        if (s.kind === "script") {
                          handleLoadStarterScriptChip(s.label);
                        } else {
                          handleQuickReply(s.label);
                        }
                      };
                      return (
                        <Chip
                          key={s.label}
                          label={s.label}
                          variant="outlined"
                          size="small"
                          disabled={isLoading}
                          onClick={handleClick}
                          icon={isLoading ? <CircularProgress size={12} thickness={5} /> : undefined}
                          sx={{
                            fontSize: "0.8125rem",
                            height: 32,
                            cursor: "pointer",
                            flexShrink: 0,
                            "& .MuiChip-label": { px: 1.5 },
                          }}
                        />
                      );
                    })}
                  </Box>

                  {/* Marie intro */}
                  <Typography variant="body2" sx={{ color: "text.disabled", lineHeight: 1.6, mb: 2.5 }}>
                    I'm Marie — your AI coding assistant for the Thermo Fisher Maps
                    platform. I can help you write, debug, and understand Maps Script
                    Bridge scripts.
                  </Typography>

                  {/* Input — lives here in hero state (centered); moves to bottom on first send */}
                  {inputContent}
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  px: 1.5,
                  pt: 1,
                  // Extra bottom padding inside the scroll container so the
                  // last message bubble or the (now larger) Marie indicator
                  // card — including its pulse halo (~6 px outset shadow) —
                  // never butts up against the chat input divider when
                  // scrolled to bottom. Belt-and-suspenders alongside the
                  // scroll-into-view dep update above.
                  pb: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                }}
              >
                {messages.map(renderMessageBubble)}

                {/* ── "Marie is thinking…" / "writing your script…" pill ──
                    Surfaces during the silent gap between Marie's chat tokens
                    and the code_update event (LLM tool-use composing the
                    script — typically 3–8 s). Hidden when:
                      - no stream is open
                      - stream is open but tokens are still flowing
                      - stream is pre-first-token (legacy "dots" indicator
                        below covers that case so we don't double up)
                      - stream has finished (done OR error) */}
                {(() => {
                  // Are we in the SILENT phase — i.e. no tokens visibly
                  // streaming right now? True before the first token
                  // (lastTokenAt === null) and during any post-token silence
                  // gap longer than the threshold. False while tokens are
                  // actively flowing (the streamed text itself IS the progress
                  // then — task spec: stop the reassurance once content shows).
                  const inSilentPhase =
                    lastTokenAt === null ||
                    tickNow - lastTokenAt > THINKING_THRESHOLD_MS;

                  // The escalating "still working" line, derived purely from
                  // elapsed wall-clock time since the request was sent. Null
                  // until the first (~15 s) threshold elapses, so fast replies
                  // never show it. Only surfaced while silent — once tokens
                  // flow the streamed text takes over. Ephemeral — never
                  // written to chat history.
                  const reassurance = inSilentPhase ? reassuranceLine : null;

                  const shouldShowThinking =
                    isStreaming &&
                    (codeUpdateReceived ||
                      expectingCode ||
                      (lastTokenAt !== null && tickNow - lastTokenAt > THINKING_THRESHOLD_MS) ||
                      // Long SILENT pre-first-token wait (reasoning models can
                      // sit quiet 20–60 s). Once a reassurance line is due,
                      // promote the full pill so it carries the line — the tiny
                      // typing dots below stand down (see their guard) to avoid
                      // a double indicator. Gated on inSilentPhase so a
                      // long-but-actively-streaming response doesn't resurrect
                      // the pill on top of flowing text.
                      reassurance !== null);
                  if (!shouldShowThinking) return null;
                  const isCodingMode = expectingCode || codeUpdateReceived;
                  const label = isCodingMode
                    ? "I'm writing your script…"
                    : "I'm thinking…";

                  // Shared shell — same outer card shape for both modes so the
                  // transition between "thinking" and "writing your script"
                  // feels like a content swap, not a rebuild. ~2x the prior
                  // pill: bigger surface, primary-tinted background, soft
                  // pulse on the surrounding card so the eye stays here while
                  // Marie's tool-call composes the script (5–15 s on long
                  // generations now that max_tokens is 16K).
                  return (
                    <Box
                      role="status"
                      aria-live="polite"
                      sx={{
                        alignSelf: "flex-start",
                        ml: "34px", // align under the bubble (avatar 28px + gap 0.75 × 8 = 6px ≈ 34px)
                        maxWidth: "min(360px, calc(100% - 40px))",
                        px: 1.5,
                        py: 1.25,
                        borderRadius: "16px",
                        border: (theme) =>
                          `1px solid ${alpha(
                            theme.palette.primary.main,
                            theme.palette.mode === "dark" ? 0.32 : 0.22,
                          )}`,
                        bgcolor: (theme) =>
                          theme.palette.mode === "dark"
                            ? alpha(theme.palette.primary.main, 0.14)
                            : alpha(theme.palette.primary.main, 0.07),
                        // Column so the optional "still working" reassurance
                        // line can stack beneath the animated content row. The
                        // inner row Box restores the previous center-aligned,
                        // gapped horizontal layout for the dots/code animation.
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        gap: 0.75,
                        // Soft pulse on the whole card — slow, primary-tinted
                        // shadow that breathes once every 2.4s. Communicates
                        // "still working" without strobing. Disabled under
                        // prefers-reduced-motion.
                        "@keyframes marieIndicatorPulse": {
                          "0%, 100%": {
                            boxShadow: (theme) =>
                              `0 0 0 0 ${alpha(theme.palette.primary.main, 0)}`,
                          },
                          "50%": {
                            boxShadow: (theme) =>
                              `0 0 0 6px ${alpha(theme.palette.primary.main, 0.08)}`,
                          },
                        },
                        animation: "marieIndicatorPulse 2.4s ease-in-out infinite",
                        "@media (prefers-reduced-motion: reduce)": {
                          animation: "none",
                        },
                      }}
                    >
                      {/* Animated content row — center-aligned, gapped, exactly
                          as before the column wrapper was introduced. */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.25,
                          width: "100%",
                        }}
                      >
                      {isCodingMode ? (
                        // ── CODING MODE ─────────────────────────────────
                        // Mock "code editor" surface: three stacked monospace
                        // lines with indent depth, fading in sequence as if
                        // being typed. A scan line sweeps top→bottom over the
                        // block. The label sits on the right with a blinking
                        // caret, monospace, so the whole thing reads as
                        // "code is being generated" rather than "text is
                        // being typed".
                        <>
                          <Box
                            aria-hidden
                            sx={{
                              position: "relative",
                              flexShrink: 0,
                              width: 92,
                              height: 56,
                              borderRadius: "8px",
                              overflow: "hidden",
                              bgcolor: (theme) =>
                                theme.palette.mode === "dark"
                                  ? alpha(theme.palette.common.black, 0.35)
                                  : alpha(theme.palette.common.black, 0.04),
                              border: (theme) =>
                                `1px solid ${alpha(
                                  theme.palette.primary.main,
                                  theme.palette.mode === "dark" ? 0.28 : 0.18,
                                )}`,
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                              gap: "5px",
                              px: 1,
                              py: 0.75,
                              // Sequential fade-in — three "code lines" that
                              // settle into view one after another, then loop.
                              "@keyframes marieCodeLineFade": {
                                "0%": { opacity: 0, transform: "translateX(-4px)" },
                                "20%": { opacity: 1, transform: "translateX(0)" },
                                "85%": { opacity: 1, transform: "translateX(0)" },
                                "100%": { opacity: 0.15, transform: "translateX(0)" },
                              },
                              // Scan line — 1px primary-tinted bar sweeping
                              // top→bottom across the card. Subtle, not flashy.
                              "@keyframes marieCodeScan": {
                                "0%": { transform: "translateY(-4px)", opacity: 0 },
                                "20%": { opacity: 0.9 },
                                "80%": { opacity: 0.9 },
                                "100%": { transform: "translateY(56px)", opacity: 0 },
                              },
                              "&::after": {
                                content: '""',
                                position: "absolute",
                                left: 0,
                                right: 0,
                                top: 0,
                                height: "1px",
                                bgcolor: (theme) =>
                                  alpha(theme.palette.primary.main, 0.85),
                                boxShadow: (theme) =>
                                  `0 0 6px 1px ${alpha(theme.palette.primary.main, 0.55)}`,
                                animation: "marieCodeScan 2.2s linear infinite",
                                "@media (prefers-reduced-motion: reduce)": {
                                  animation: "none",
                                  opacity: 0,
                                },
                              },
                            }}
                          >
                            {/* Three stacked "code lines" with varying widths
                                and indent depth — top-level, indented,
                                deeper-indented — to suggest a function body
                                being scaffolded. Bars rather than glyphs so
                                the visual reads at a glance even small. */}
                            {[
                              { width: "62%", indent: 0, delay: 0 },
                              { width: "78%", indent: 10, delay: 0.35 },
                              { width: "44%", indent: 20, delay: 0.7 },
                            ].map((line, i) => (
                              <Box
                                key={i}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                  pl: `${line.indent}px`,
                                }}
                              >
                                {line.indent > 0 && (
                                  <Box
                                    sx={{
                                      width: "1px",
                                      height: "8px",
                                      bgcolor: (theme) =>
                                        alpha(theme.palette.text.secondary, 0.35),
                                    }}
                                  />
                                )}
                                <Box
                                  sx={{
                                    flex: `0 0 ${line.width}`,
                                    height: "6px",
                                    borderRadius: "2px",
                                    bgcolor: (theme) =>
                                      i === 0
                                        ? alpha(theme.palette.primary.main, 0.85)
                                        : alpha(theme.palette.text.secondary, 0.55),
                                    animation: `marieCodeLineFade 1.8s ease-in-out ${line.delay}s infinite`,
                                    "@media (prefers-reduced-motion: reduce)": {
                                      animation: "none",
                                      opacity: 0.7,
                                    },
                                  }}
                                />
                              </Box>
                            ))}
                          </Box>

                          <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                            <Typography
                              sx={{
                                fontFamily:
                                  '"JetBrains Mono", "Fira Code", "Consolas", "Menlo", monospace',
                                fontSize: "0.8125rem",
                                fontWeight: 500,
                                color: "primary.main",
                                lineHeight: 1.3,
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              {label}
                              <Box
                                aria-hidden
                                component="span"
                                sx={{
                                  display: "inline-block",
                                  width: "7px",
                                  height: "14px",
                                  bgcolor: "primary.main",
                                  ml: "2px",
                                  "@keyframes marieCodeCaret": {
                                    "0%, 50%": { opacity: 1 },
                                    "51%, 100%": { opacity: 0 },
                                  },
                                  animation: "marieCodeCaret 1s steps(1, end) infinite",
                                  "@media (prefers-reduced-motion: reduce)": {
                                    animation: "none",
                                    opacity: 0.6,
                                  },
                                }}
                              />
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                fontFamily:
                                  '"JetBrains Mono", "Fira Code", "Consolas", "Menlo", monospace',
                                fontSize: "0.6875rem",
                                color: "text.secondary",
                                mt: 0.25,
                              }}
                            >
                              {codeUpdateReceived ? "// streaming to editor" : "// composing"}
                            </Typography>
                          </Box>
                        </>
                      ) : (
                        // ── THINKING MODE ──────────────────────────────
                        // Three larger bouncing dots with the avatar dot —
                        // the classic typing pattern at ~2x the prior size.
                        // Slow bounce (1.4s) so it reads as "deliberate
                        // thought" rather than rapid-fire input.
                        <>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              "@keyframes marieThinkingBounce": {
                                "0%, 80%, 100%": {
                                  transform: "translateY(0)",
                                  opacity: 0.35,
                                },
                                "40%": {
                                  transform: "translateY(-6px)",
                                  opacity: 1,
                                },
                              },
                            }}
                          >
                            {[0, 1, 2].map((i) => (
                              <Box
                                key={i}
                                aria-hidden
                                sx={{
                                  width: 10,
                                  height: 10,
                                  borderRadius: "50%",
                                  bgcolor: "primary.main",
                                  animation: `marieThinkingBounce 1.4s ease-in-out ${i * 0.18}s infinite`,
                                  "@media (prefers-reduced-motion: reduce)": {
                                    animation: "none",
                                    opacity: 0.55,
                                    transform: "none",
                                  },
                                }}
                              />
                            ))}
                          </Box>
                          <Typography
                            sx={{
                              fontStyle: "italic",
                              fontSize: "0.875rem",
                              color: "text.secondary",
                              lineHeight: 1.2,
                            }}
                          >
                            {label}
                          </Typography>
                        </>
                      )}
                      </Box>

                      {/* ── Periodic "still working" reassurance ──────────
                          Ephemeral, time-derived line in Marie's voice. Only
                          renders once the elapsed-time threshold makes it
                          non-null (≥15 s). Updates in place as the wait
                          escalates (~30 s, ~45 s). NOT a chat message — it
                          lives only here and vanishes on done/error/reset.
                          aria-live on the parent card announces the change to
                          screen readers. No animation of its own, so it works
                          under prefers-reduced-motion. */}
                      {reassurance && (
                        <Typography
                          sx={{
                            fontSize: "0.8125rem",
                            fontStyle: "italic",
                            color: "text.secondary",
                            lineHeight: 1.35,
                            pl: "2px",
                          }}
                        >
                          {reassurance}
                        </Typography>
                      )}
                    </Box>
                  );
                })()}

                {/* Typing indicator — small pre-first-token dots. Stands down
                    once a "still working" reassurance line is due, since the
                    larger thinking pill above then takes over and carries it
                    (otherwise we'd render two stacked indicators). */}
                {isStreaming &&
                  messages[messages.length - 1]?.content === "" &&
                  reassuranceLine === null && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <Box
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        bgcolor: "secondary.main",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <MarieIcon fontSize={14} color="rgba(0,0,0,0.87)" />
                    </Box>
                    <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                      {[0, 1, 2].map((i) => (
                        <Box
                          key={i}
                          sx={{
                            width: 5,
                            height: 5,
                            borderRadius: "50%",
                            bgcolor: "text.disabled",
                            "@keyframes typingDot": {
                              "0%, 80%, 100%": { opacity: 0.2 },
                              "40%": { opacity: 1 },
                            },
                            animation: `typingDot 1.2s ease-in-out ${i * 0.2}s infinite`,
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                <div ref={messagesEndRef} />
              </Box>
            )}

            {/* Input — pinned to bottom in active chat; slides in from above on first transition */}
            {!isHeroState && (
              <>
                <Divider />
                <Box
                  sx={{
                    px: 1.5,
                    py: 1,
                    flexShrink: 0,
                    ...(inputSlideIn && {
                      "@keyframes floatToBottom": {
                        from: { transform: "translateY(-120px)", opacity: 0 },
                        to: { transform: "translateY(0)", opacity: 1 },
                      },
                      animation: "floatToBottom 0.9s cubic-bezier(0.22, 1, 0.36, 1)",
                    }),
                  }}
                >
                  {inputContent}
                </Box>
              </>
            )}
          </>
        );
      })()}
    </Box>

    {/* ── Restore-confirmation dialog ─────────────────────────
        Surfaces before any restore actually lands, so the user can't
        silently lose unsaved manual edits to the editor. We don't
        diff against the editor — keeping the prompt unconditional is
        simpler than implementing a "dirty" detector and it normalizes
        the click → toast feedback shape for both clean and dirty cases.
        Hosted inside panelContent so both AiPanel render branches
        (Chat-mode fullWidth, Code-mode sidebar) get the same affordance. */}
    <Dialog
      open={pendingRestore !== null}
      onClose={() => setPendingRestore(null)}
      aria-labelledby="restore-confirm-title"
    >
      <DialogTitle id="restore-confirm-title">Restore earlier code?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          This will replace your current editor content with this earlier version
          from the chat. Any unsaved changes in the editor will be lost.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setPendingRestore(null)}>Cancel</Button>
        <Button
          onClick={handleConfirmRestore}
          variant="contained"
          color="primary"
          startIcon={<RestoreOutlinedIcon sx={{ fontSize: 16 }} />}
          autoFocus
        >
          Restore
        </Button>
      </DialogActions>
    </Dialog>

    {/* ── Restore confirmation snackbar ───────────────────────
        Belt-and-suspenders next to the brief inline "Restored" chip
        flash. The chip flash answers "did my click register?" in the
        bubble the user just clicked; the snackbar answers "what changed?"
        across the whole workspace, including the editor that just got
        clobbered. Auto-dismisses fast — this is in-session only, no
        action surface needed. */}
    <Snackbar
      open={restoreSnackbarOpen}
      autoHideDuration={2400}
      onClose={() => setRestoreSnackbarOpen(false)}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
    >
      <Alert
        onClose={() => setRestoreSnackbarOpen(false)}
        severity="success"
        variant="filled"
        sx={{ width: "100%" }}
      >
        Restored earlier code
      </Alert>
    </Snackbar>
    </>
  );

  // In full-width (Chat) mode: no fixed width, no drag handle — fills parent
  if (fullWidth) {
    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "row",
          height: "100%",
          overflow: "hidden",
          minWidth: 0,
        }}
      >
        {panelContent}
      </Box>
    );
  }

  // Normal sidebar mode
  return (
    <Box
      sx={{
        width,
        minWidth: MIN_PANEL_WIDTH,
        flexShrink: 0,
        display: "flex",
        flexDirection: "row",
        height: "100%",
        position: "relative",
      }}
    >
      {/* ── Drag handle ─────────────────────────────────────── */}
      <Box
        onMouseDown={handleMouseDown}
        sx={{
          width: 4,
          cursor: "col-resize",
          bgcolor: "divider",
          flexShrink: 0,
          "&:hover": { bgcolor: "primary.main" },
          transition: "background-color 150ms",
          userSelect: "none",
        }}
        aria-hidden
      />

      {panelContent}
    </Box>
  );
}
